// src/pages/WeeklyActivityAdmin.js
// 週活動後台：一條一條呈現，表單可新增/編輯，多張圖片上傳

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import cardBackImg from '../assets/card_back.png';
import OrganizerAccountsSection from '../components/OrganizerAccountsSection';
import OrganizerReviewNotifySection from '../components/OrganizerReviewNotifySection';
import OrganizerReviewDrawer from '../components/OrganizerReviewDrawer';
import OrganizerProductReviewDrawer from '../components/OrganizerProductReviewDrawer';
import WeeklyActivityPreviewPanel from '../components/WeeklyActivityPreviewPanel';
import WeeklyActivityCardPrintPanel from '../components/WeeklyActivityCardPrintPanel';
import QuarterHourTimeInput from '../components/QuarterHourTimeInput';
import TimeAvailabilityBar from '../components/TimeAvailabilityBar';
import SuggestMultilineField from '../components/SuggestMultilineField';
import EquipmentNeedMultiSelect from '../components/EquipmentNeedMultiSelect';
import {
  activityToPreviewModel,
  getActivitySourceLabel,
  normalizeTimeToQuarter,
} from '../lib/weeklyActivityFormUtils';
import {
  buildDayAvailabilitySlots,
  buildMonthDayStatus,
  withRangePickClickable,
  applyRangeSlotClick,
  allowedStartTimes,
  allowedStartTimesForDuration,
  parseHmToMinutes,
} from '../lib/venueDayAvailability';
import {
  defaultVenueEndFromStart,
  clampActivityStartToVenue,
  activityStartOptionsInVenueWindow,
  activityEndOptionsAfterStart,
  defaultActivityEnd,
  formatActivityTimeRange,
  normalizeEndMinutesAfterStart,
  isCrossMidnightHm,
  ORGANIZER_VENUE_MAX_MINUTES,
  ADMIN_VENUE_MAX_MINUTES,
  venueWindowDurationMinutes,
  sliceYmd,
  minsToHm,
} from '../lib/activityVenueTimes';
import { toastMessage } from '../lib/toast';

function pad2(n) {
  return String(Number(n) || 0).padStart(2, '0');
}

function ActivitySourceBadge({ source, style: extraStyle }) {
  const { kind, label, detail } = getActivitySourceLabel(source);
  const bg = kind === 'organizer' ? '#e6f4ff'
    : kind === 'assist' ? '#fff7e6'
      : kind === 'unbound' ? '#fffbeb'
        : '#f6ffed';
  const color = kind === 'organizer' ? '#0958d9'
    : kind === 'assist' ? '#d46b08'
      : kind === 'unbound' ? '#b45309'
        : '#389e0d';
  const border = kind === 'organizer' ? '#91caff'
    : kind === 'assist' ? '#ffd591'
      : kind === 'unbound' ? '#fcd34d'
        : '#b7eb8f';
  return (
    <span style={{
      display: 'inline-block',
      fontSize: 12,
      fontWeight: 600,
      padding: '2px 8px',
      borderRadius: 6,
      background: bg,
      color,
      border: `1px solid ${border}`,
      marginLeft: 8,
      ...extraStyle,
    }}
    >
      {label}{detail}
    </span>
  );
}

const fileToBase64 = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

const todayYmd = () => {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const emptyForm = () => ({
  timeSlots: [{
    id: null,
    date: todayYmd(),
    time: '19:30',
    endTime: defaultVenueEndFromStart('19:30'),
    venueEndDate: '',
    activityTime: '19:30',
    activityEndTime: defaultVenueEndFromStart('19:30'),
    activityEndDate: '',
  }],
  venue: '',
  name: '',
  name_en: '',
  organizer: '',
  detail: '',
  activity_flow: '',
  consumption_type: '',
  status: 'published',
  is_public: true,
  is_internal: true,
  occupies_full_venue: false,
  venue_ordered_separately: false,
  beverage_need: '',
  handover_notes: '',
  internal_brief: '',
  equipment_need: '',
  registration_link: '',
  venueBookingRequested: false,
  images: [],
  organizer_id: null,
  organizer_account_name: null,
  batch_group: null,
  _originalSlotIds: [],
});

/** API 列 → 表單時段（場地起迄 vs 活動起迄） */
function slotFromApiRow(r, opts = {}) {
  const toTimeVal = (raw, fallback = '19:30') => {
    const s = (raw || '').toString().trim();
    return normalizeTimeToQuarter(s.length >= 5 ? s.slice(0, 5) : fallback);
  };
  const activityRaw = r?.activity_time ?? r?.activityTime ?? r?.ACTIVITY_TIME;
  const activityEndRaw = r?.end_time ?? r?.endTime ?? r?.END_TIME;
  const venueStartRaw = r?.venue_start_time ?? r?.venueStartTime ?? r?.VENUE_START_TIME;
  const venueEndRaw = r?.venue_end_time ?? r?.venueEndTime ?? r?.VENUE_END_TIME;
  const venueEndDateRaw = r?.venue_end_date ?? r?.venueEndDate ?? r?.VENUE_END_DATE;
  const activityEndDateRaw = r?.activity_end_date ?? r?.activityEndDate ?? r?.ACTIVITY_END_DATE;
  const hasVenue = !!(venueStartRaw || venueEndRaw);
  const date = r?.activity_date || todayYmd();
  const venueEndDate = sliceYmd(venueEndDateRaw) || '';
  const activityEndDate = sliceYmd(activityEndDateRaw) || '';
  const time = hasVenue
    ? toTimeVal(venueStartRaw, toTimeVal(activityRaw))
    : '';
  const endParsed = hasVenue
    ? normalizeTimeToQuarter((venueEndRaw || '').toString().trim().slice(0, 5) || '')
    : '';
  const endTime = hasVenue
    ? (endParsed || defaultVenueEndFromStart(time || toTimeVal(activityRaw)))
    : '';
  const activityTimeBase = toTimeVal(activityRaw, time || '19:30');
  const activityTime = hasVenue && time && endTime
    ? clampActivityStartToVenue(activityTimeBase, time, endTime, date, venueEndDate || null)
    : activityTimeBase;
  const activityEndParsed = normalizeTimeToQuarter(
    (activityEndRaw || '').toString().trim().slice(0, 5) || '',
  );
  const activityEndTime = activityEndParsed
    || defaultActivityEnd(endTime, activityTime);
  return {
    id: opts.id !== undefined ? opts.id : (r?.id ?? null),
    date,
    time,
    endTime,
    venueEndDate,
    activityTime,
    activityEndTime,
    activityEndDate,
    vb_order_id: r?.vb_order_id ?? r?.vbOrderId ?? r?.VB_ORDER_ID ?? null,
  };
}

function formatVenueOptionLabel(venue, code) {
  const v = String(venue || '').trim();
  const c = String(code || '').trim();
  return c ? `[${c}] ${v}` : v;
}

function normalizeHexColor(raw, fallback = '#4ade80') {
  const s = String(raw || '').trim();
  if (/^#[0-9A-Fa-f]{6}$/.test(s)) return s.toLowerCase();
  if (/^[0-9A-Fa-f]{6}$/.test(s)) return `#${s.toLowerCase()}`;
  if (/^#[0-9A-Fa-f]{3}$/.test(s)) {
    const h = s.slice(1);
    return `#${h[0]}${h[0]}${h[1]}${h[1]}${h[2]}${h[2]}`.toLowerCase();
  }
  return fallback;
}

// 通用日期格式化
function dateToYmd(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${dd}`;
}

/** m0：0–11 月索引，回傳該曆月首日與末日 YYYY-MM-DD */
function monthBoundsYmd(y, m0) {
  const m = m0 + 1;
  const pad = (n) => String(n).padStart(2, '0');
  const last = new Date(y, m, 0).getDate();
  return {
    start: `${y}-${pad(m)}-01`,
    end: `${y}-${pad(m)}-${pad(last)}`,
  };
}

function isFullCalendarMonthRange(startYmd, endYmd) {
  if (!startYmd || !endYmd || startYmd > endYmd) return false;
  const [y1, m1, d1] = startYmd.split('-').map(Number);
  const [y2, m2, d2] = endYmd.split('-').map(Number);
  if (y1 !== y2 || m1 !== m2 || d1 !== 1) return false;
  const last = new Date(y2, m2, 0).getDate();
  return d2 === last;
}

/** 週一為一列之首：(日=0)→ 0=週一 */
function mondayWeekIndex(d) {
  return (d.getDay() + 6) % 7;
}

/** 產生月曆網格（6 週 × 7 天），含前後月補格；每格 { ymd, inMonth } */
function buildCalendarGrid(viewYear, viewMonth0) {
  const first = new Date(viewYear, viewMonth0, 1);
  const lead = mondayWeekIndex(first);
  const start = new Date(viewYear, viewMonth0, 1 - lead);
  const cells = [];
  for (let i = 0; i < 42; i += 1) {
    const d = new Date(start);
    d.setDate(start.getDate() + i);
    const ymd = dateToYmd(d);
    const inMonth = d.getMonth() === viewMonth0 && d.getFullYear() === viewYear;
    cells.push({ d, ymd, inMonth });
  }
  return cells;
}

// Canvas 圓角矩形
function drawRoundRect(ctx, x, y, w, h, r) {
  ctx.beginPath();
  ctx.moveTo(x + r, y);
  ctx.lineTo(x + w - r, y);
  ctx.arcTo(x + w, y, x + w, y + r, r);
  ctx.lineTo(x + w, y + h - r);
  ctx.arcTo(x + w, y + h, x + w - r, y + h, r);
  ctx.lineTo(x + r, y + h);
  ctx.arcTo(x, y + h, x, y + h - r, r);
  ctx.lineTo(x, y + r);
  ctx.arcTo(x, y, x + r, y, r);
  ctx.closePath();
}

function OrganizerFinanceSection({ adminAuthHeaders, formSectionStyle }) {
  const [rows, setRows] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const [from, setFrom] = React.useState(() => { const d = new Date(); return `${d.getFullYear()}-01-01`; });
  const [to, setTo] = React.useState(() => { const d = new Date(); return `${d.getFullYear()}-12-31`; });

  React.useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await fetch(`/api/vb/admin/organizer-finance?from=${from}&to=${to}`, { headers: adminAuthHeaders() });
        const data = await res.json();
        if (data?.ok) setRows(data.data || []);
      } catch { /* ignore */ }
      setLoading(false);
    })();
  }, [from, to, adminAuthHeaders]);

  const byOrg = React.useMemo(() => {
    const m = {};
    for (const r of rows) {
      const oid = r.ORGANIZER_ID ?? r.organizerId ?? 'unknown';
      if (!m[oid]) m[oid] = { name: r.ORGANIZER_DISPLAY_NAME ?? r.organizerDisplayName ?? r.ORGANIZER_LOGIN ?? r.organizerLogin ?? '', total: 0, orders: 0, cash: 0, credit: 0, items: [] };
      const amt = Number(r.TOTAL_AMOUNT ?? r.totalAmount ?? 0);
      const cnt = Number(r.ORDER_COUNT ?? r.orderCount ?? 0);
      m[oid].total += amt;
      m[oid].orders += cnt;
      if ((r.PAYMENT_METHOD ?? r.paymentMethod) === 'cash') m[oid].cash += amt;
      else m[oid].credit += amt;
      m[oid].items.push(r);
    }
    return Object.values(m);
  }, [rows]);

  const fmt = (n) => `NT$ ${Number(n || 0).toLocaleString()}`;

  return (
    <section style={{ ...formSectionStyle, marginBottom: 24 }}>
      <h2 style={{ marginTop: 0, fontSize: 18 }}>主辦財務報表</h2>
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <label>起始 <input type="date" value={from} onChange={(e) => setFrom(e.target.value)} style={{ padding: '4px 8px' }} /></label>
        <label>結束 <input type="date" value={to} onChange={(e) => setTo(e.target.value)} style={{ padding: '4px 8px' }} /></label>
      </div>
      {loading ? <p>載入中…</p> : byOrg.length === 0 ? <p style={{ color: '#888' }}>此期間無資料</p> : (
        <div style={{ overflowX: 'auto', WebkitOverflowScrolling: 'touch', maxWidth: '100%' }}>
        <table style={{ width: '100%', minWidth: 560, borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f0f0f0' }}>
              <th style={{ textAlign: 'left', padding: 8 }}>主辦</th>
              <th style={{ textAlign: 'right', padding: 8 }}>人數</th>
              <th style={{ textAlign: 'right', padding: 8 }}>現金</th>
              <th style={{ textAlign: 'right', padding: 8 }}>信用卡</th>
              <th style={{ textAlign: 'right', padding: 8 }}>總計</th>
            </tr>
          </thead>
          <tbody>
            {byOrg.map((o, i) => (
              <tr key={i} style={{ borderBottom: '1px solid #eee' }}>
                <td style={{ padding: 8 }}>{o.name}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{o.orders}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{fmt(o.cash)}</td>
                <td style={{ padding: 8, textAlign: 'right' }}>{fmt(o.credit)}</td>
                <td style={{ padding: 8, textAlign: 'right', fontWeight: 700 }}>{fmt(o.total)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </section>
  );
}

export default function WeeklyActivityAdmin() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm] = useState(emptyForm());
  const [editingId, setEditingId] = useState(null);
  const [detachSingle, setDetachSingle] = useState(false);
  const [batchEditChoice, setBatchEditChoice] = useState(null);
  /** 複製模式：{ sourceName }；非複製為 null */
  const [copyMode, setCopyMode] = useState(null);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [organizerOptions, setOrganizerOptions] = useState([]);
  const [filterUnboundOrganizer, setFilterUnboundOrganizer] = useState(false);

  const formPreview = useMemo(() => {
    const slot = form.timeSlots?.[0] || {};
    return activityToPreviewModel({
      ...form,
      activity_date: slot.date,
      activity_time: slot.activityTime || slot.time,
      end_time: slot.activityEndTime || '',
      venue_start_time: slot.time || '',
      venue_end_time: slot.endTime || '',
    });
  }, [form]);

  const consumptionSuggestions = useMemo(
    () => [...new Set(list.map((r) => r.consumption_type).filter(Boolean))],
    [list],
  );

  // 場地顏色
  const [venueColors, setVenueColors] = useState([]);
  const [rentalProducts, setRentalProducts] = useState([]);
  const [vcVenue, setVcVenue] = useState('');
  const [vcColor, setVcColor] = useState('#4ade80');
  const [vcShortName, setVcShortName] = useState('');
  const [vcVenueEn, setVcVenueEn] = useState('');
  const [vcShortNameEn, setVcShortNameEn] = useState('');
  const [vcCode, setVcCode] = useState('');
  const [vcProductId, setVcProductId] = useState('');
  const [vcSaving, setVcSaving] = useState(false);
  const [vcExpanded, setVcExpanded] = useState(false);

  // 飲品／交接建議
  const [beverageSuggestions, setBeverageSuggestions] = useState([]);
  const [handoverSuggestions, setHandoverSuggestions] = useState([]);
  const [internalBriefSuggestions, setInternalBriefSuggestions] = useState([]);
  const [equipmentSuggestions, setEquipmentSuggestions] = useState([]);

  // 產出週活動圖
  const [imgExpanded, setImgExpanded] = useState(false);
  const [weekPickerDate, setWeekPickerDate] = useState(() => todayYmd());
  const [generating, setGenerating] = useState(false);
  /** 週活動圖預覽（單張長圖；陣列僅保留一張 url 以沿用下載邏輯） */
  const [previewPages, setPreviewPages] = useState([]);
  const canvasRef = useRef(null);

  // 活動列表：日期區間（預設當月整月）＋主辦人多選
  const [filterDateStart, setFilterDateStart] = useState(() => {
    const d = new Date();
    return monthBoundsYmd(d.getFullYear(), d.getMonth()).start;
  });
  const [filterDateEnd, setFilterDateEnd] = useState(() => {
    const d = new Date();
    return monthBoundsYmd(d.getFullYear(), d.getMonth()).end;
  });
  /** 主辦人複選：空陣列表示不篩主辦人 */
  const [filterOrganizerSet, setFilterOrganizerSet] = useState([]);
  /** 篩選月曆顯示的月份（可與已選區間不同，方便切換檢視） */
  const [filterCalView, setFilterCalView] = useState(() => {
    const d = new Date();
    return { y: d.getFullYear(), m0: d.getMonth() };
  });
  const [orgFilterOpen, setOrgFilterOpen] = useState(false);
  const orgFilterWrapRef = useRef(null);
  /** 拖曳選日期區間：preview 僅拖曳中顯示 */
  const rangeDragRef = useRef(null);
  const [rangeDragPreview, setRangeDragPreview] = useState(null);

  // 詳情彈窗
  const [detailItem, setDetailItem] = useState(null);

  /** 主辦送審月曆（須 admin JWT） */
  const [adminTab, setAdminTab] = useState('activities');
  const [waNarrow, setWaNarrow] = useState(() => typeof window !== 'undefined' && window.innerWidth < 768);
  const [mobileShowForm, setMobileShowForm] = useState(false);
  const [filterExpanded, setFilterExpanded] = useState(false);
  useEffect(() => {
    const onR = () => setWaNarrow(window.innerWidth < 768);
    window.addEventListener('resize', onR);
    return () => window.removeEventListener('resize', onR);
  }, []);
  const [pendingActionCount, setPendingActionCount] = useState(0);
  const [pendingCalendar, setPendingCalendar] = useState([]);
  const [pendingLoading, setPendingLoading] = useState(false);
  const [pendingRejectId, setPendingRejectId] = useState(null);
  const [pendingRejectKind, setPendingRejectKind] = useState('calendar');
  const [pendingRejectReason, setPendingRejectReason] = useState('');
  const [reviewCalendarId, setReviewCalendarId] = useState(null);
  const [reviewProductId, setReviewProductId] = useState(null);

  // 主辦送審篩選
  const [pendingDateFrom, setPendingDateFrom] = useState('');
  const [pendingDateTo, setPendingDateTo] = useState('');
  const [pendingOrganizerId, setPendingOrganizerId] = useState('');
  const [pendingVenue, setPendingVenue] = useState('');
  const [pendingStatus, setPendingStatus] = useState(['pending', 'rejected']);
  const [pendingResubmitOnly, setPendingResubmitOnly] = useState(false);
  const [pendingIncludeApproved, setPendingIncludeApproved] = useState(false);
  const pendingCalendarDisplay = useMemo(() => {
    if (!pendingResubmitOnly) return pendingCalendar;
    return pendingCalendar.filter((r) => {
      const phase = r.REVIEW_PHASE || r.reviewPhase || '';
      return phase === 'resubmit_after_reject';
    });
  }, [pendingCalendar, pendingResubmitOnly]);
  const [passGrantModal, setPassGrantModal] = useState(null); // { activityId, action: 'issue'|'reissue', memberLabel }
  const [passGrantSaving, setPassGrantSaving] = useState(false);

  const adminAuthHeaders = () => {
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('admin_token') : '';
    return token ? { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' } : {};
  };

  const loadPendingCalendar = useCallback(async () => {
    setPendingLoading(true);
    try {
      const qs = new URLSearchParams();
      if (pendingDateFrom) qs.set('dateFrom', pendingDateFrom);
      if (pendingDateTo) qs.set('dateTo', pendingDateTo);
      if (pendingOrganizerId) qs.set('organizerId', pendingOrganizerId);
      if (pendingVenue) qs.set('venue', pendingVenue);
      if (pendingStatus.length) qs.set('status', pendingStatus.join(','));
      if (pendingIncludeApproved) qs.set('includeApproved', '1');
      const url = `/api/vb/admin/organizer-pending${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url, { headers: adminAuthHeaders() });
      const data = await res.json();
      if (data?.ok) setPendingCalendar(data.data || []);
      else {
        setPendingCalendar([]);
        toastMessage(data?.error || '載入主辦送審失敗', 'error');
      }
    } catch (e) {
      console.error(e);
      setPendingCalendar([]);
      toastMessage('載入主辦送審失敗', 'error');
    } finally {
      setPendingLoading(false);
    }
  }, [pendingDateFrom, pendingDateTo, pendingOrganizerId, pendingVenue, pendingStatus, pendingIncludeApproved]);

  /** 分頁紅點：待審核／改版待審／待發日券 */
  const loadPendingActionCount = useCallback(async () => {
    try {
      const qs = new URLSearchParams({ status: 'pending,ended_pass_pending' });
      const res = await fetch(`/api/vb/admin/organizer-pending?${qs}`, { headers: adminAuthHeaders() });
      const data = await res.json();
      if (data?.ok) setPendingActionCount((data.data || []).length);
      else setPendingActionCount(0);
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    loadPendingActionCount();
    const t = setInterval(loadPendingActionCount, 60000);
    return () => clearInterval(t);
  }, [loadPendingActionCount]);

  const refreshPending = useCallback(() => {
    loadPendingCalendar();
    loadPendingActionCount();
  }, [loadPendingCalendar, loadPendingActionCount]);

  useEffect(() => {
    if (adminTab === 'organizer_pending') {
      refreshPending();
    }
  }, [adminTab, refreshPending]);

  useEffect(() => {
    if (typeof window === 'undefined') return;
    const params = new URLSearchParams(window.location.search);
    const tab = params.get('tab');
    const highlight = params.get('highlight');
    if (tab === 'organizer_pending' || tab === 'organizer_accounts' || tab === 'organizer_finance' || tab === 'organizer_review_notify') {
      setAdminTab(tab);
    }
    if (highlight && tab === 'organizer_pending') {
      const n = parseInt(highlight, 10);
      if (Number.isFinite(n)) setReviewCalendarId(n);
    }
  }, []);

  const openReview = (row) => {
    const kind = row.PENDING_KIND ?? row.pendingKind ?? 'calendar';
    if (kind === 'product') {
      const pid = row.PRODUCT_ID ?? row.productId ?? row.VB_PRODUCT_ID ?? row.vbProductId;
      setReviewProductId(pid);
      setReviewCalendarId(null);
    } else {
      setReviewCalendarId(row.ID ?? row.id);
      setReviewProductId(null);
    }
  };

  const openReject = (row) => {
    const kind = row.PENDING_KIND ?? row.pendingKind ?? 'calendar';
    const id = kind === 'product'
      ? (row.PRODUCT_ID ?? row.productId ?? row.VB_PRODUCT_ID ?? row.vbProductId)
      : (row.ID ?? row.id);
    setPendingRejectKind(kind);
    setPendingRejectId(id);
  };

  const openPassGrantModal = (activityId, action, memberLabel) => {
    setPassGrantModal({ activityId, action, memberLabel: memberLabel || '' });
  };

  const submitPassGrantModal = async () => {
    if (!passGrantModal) return;
    setPassGrantSaving(true);
    try {
      const res = await fetch(`/api/vb/admin/organizer-activity/${passGrantModal.activityId}/pass-grant`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ action: passGrantModal.action }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '操作失敗');
      toastMessage(passGrantModal.action === 'reissue' ? '已補發 1 張日券' : '已發放 1 張日券', 'success');
      setPassGrantModal(null);
      refreshPending();
    } catch (e) {
      toastMessage(e.message || '操作失敗', 'error');
    } finally {
      setPassGrantSaving(false);
    }
  };

  const handlePassGrantSkip = async (activityId) => {
    if (!window.confirm('確定這次不發？將標為結案。')) return;
    try {
      const res = await fetch(`/api/vb/admin/organizer-activity/${activityId}/pass-grant`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ action: 'skip' }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '操作失敗');
      toastMessage('已略過並結案', 'success');
      refreshPending();
    } catch (e) {
      toastMessage(e.message || '操作失敗', 'error');
    }
  };

  const rejectPendingCalendar = async () => {
    if (!pendingRejectId) return;
    try {
      const url = pendingRejectKind === 'product'
        ? `/api/vb/admin/organizer-pending-product/${pendingRejectId}/reject`
        : `/api/vb/admin/organizer-pending/${pendingRejectId}/reject`;
      const res = await fetch(url, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ reason: pendingRejectReason }),
      });
      const data = await res.json();
      if (!data?.ok) throw new Error(data?.error || '退回失敗');
      toastMessage('已退回', 'success');
      setPendingRejectId(null);
      setPendingRejectKind('calendar');
      setPendingRejectReason('');
      refreshPending();
    } catch (e) {
      toastMessage(e.message || '退回失敗', 'error');
    }
  };

  const loadVenueColors = useCallback(async () => {
    try {
      const res = await fetch('/api/weekly-activity/venue-color/list');
      const data = await res.json();
      if (data.success) {
        setVenueColors((data.list || []).map((vc) => ({
          ...vc,
          code: vc.code || '',
          vbProductId: vc.vbProductId ?? vc.vb_product_id ?? null,
        })));
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadRentalProducts = useCallback(async () => {
    try {
      const res = await fetch('/api/weekly-activity/venue-color/rental-products');
      const data = await res.json();
      if (data.success) setRentalProducts(data.list || []);
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadFieldSuggestions = useCallback(async () => {
    try {
      const res = await fetch('/api/weekly-activity/admin/field-suggestions');
      const data = await res.json();
      if (data.success) {
        setBeverageSuggestions(data.data?.beverageNeeds || []);
        setHandoverSuggestions(data.data?.handoverNotes || []);
        setInternalBriefSuggestions(data.data?.internalBriefs || []);
        setEquipmentSuggestions(data.data?.equipmentNeeds || []);
      }
    } catch (e) {
      console.error(e);
    }
  }, []);

  const loadList = useCallback(async () => {
    setLoading(true);
    try {
      const qs = new URLSearchParams();
      if (filterDateStart) qs.set('dateFrom', filterDateStart);
      if (filterDateEnd) qs.set('dateTo', filterDateEnd);
      const url = `/api/weekly-activity/admin/list${qs.toString() ? `?${qs}` : ''}`;
      const res = await fetch(url);
      const data = await res.json();
      if (data.success) setList(data.list || []);
      else setList([]);
    } catch (e) {
      console.error(e);
      setList([]);
    } finally {
      setLoading(false);
    }
  }, [filterDateStart, filterDateEnd]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  useEffect(() => {
    loadVenueColors();
    loadRentalProducts();
    loadFieldSuggestions();
    (async () => {
      try {
        const res = await fetch('/api/vb/admin/organizers', { headers: adminAuthHeaders() });
        const data = await res.json();
        if (Array.isArray(data)) setOrganizerOptions(data);
        else if (Array.isArray(data?.data)) setOrganizerOptions(data.data);
      } catch { /* ignore */ }
    })();
  }, [loadVenueColors, loadRentalProducts, loadFieldSuggestions]);

  const rentalProductNameById = useMemo(() => {
    const m = {};
    for (const p of rentalProducts) {
      const id = p.id ?? p.ID;
      if (id != null) m[id] = p.name ?? p.NAME ?? `#${id}`;
    }
    return m;
  }, [rentalProducts]);

  const venueSelectOptions = useMemo(() => {
    const map = new Map();
    for (const vc of venueColors) {
      if (!vc.venue) continue;
      map.set(vc.venue, {
        venue: vc.venue,
        code: vc.code || '',
        color: vc.color || '',
        vbProductId: vc.vbProductId ?? null,
      });
    }
    for (const r of list) {
      if (r.venue && !map.has(r.venue)) {
        map.set(r.venue, { venue: r.venue, code: '', color: '', vbProductId: null });
      }
    }
    return [...map.values()].sort((a, b) => a.venue.localeCompare(b.venue, 'zh-Hant'));
  }, [venueColors, list]);

  const selectedVenueMeta = useMemo(
    () => venueColors.find((vc) => vc.venue === form.venue) || null,
    [venueColors, form.venue],
  );
  const venueNeedsBooking = !form.venue_ordered_separately && !!(selectedVenueMeta?.vbProductId);

  const hasVenueOrder = useMemo(() => {
    return (form.timeSlots || []).some((s) => {
      const oid = s.vb_order_id ?? s.vbOrderId ?? s.VB_ORDER_ID;
      return oid != null && oid !== '' && Number.isFinite(Number(oid));
    });
  }, [form.timeSlots]);

  const [availMonth, setAvailMonth] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${pad2(now.getMonth() + 1)}`;
  });
  const [availability, setAvailability] = useState(null);
  const [availLoading, setAvailLoading] = useState(false);
  const [focusSlotIdx, setFocusSlotIdx] = useState(0);

  /** 編輯時段可用性須排除本活動既有場地訂單，否則微調時間會被自身佔用排斥 */
  const excludeAvailOrderKey = useMemo(() => {
    const ids = (form.timeSlots || [])
      .map((s) => s.vb_order_id ?? s.vbOrderId ?? s.VB_ORDER_ID)
      .filter((id) => id != null && id !== '' && Number.isFinite(Number(id)))
      .map((id) => Number(id));
    return [...new Set(ids)].sort((a, b) => a - b).join(',');
  }, [form.timeSlots]);

  useEffect(() => {
    const pid = selectedVenueMeta?.vbProductId;
    if (!pid) {
      setAvailability(null);
      return;
    }
    const month = availMonth;
    if (!/^\d{4}-\d{2}$/.test(month)) return;
    let cancelled = false;
    (async () => {
      setAvailLoading(true);
      try {
        const qs = new URLSearchParams({ month });
        if (excludeAvailOrderKey) qs.set('excludeOrderIds', excludeAvailOrderKey);
        const res = await fetch(`/api/vb/public/products/${pid}/availability?${qs}`);
        const data = await res.json();
        if (!cancelled) {
          setAvailability(data?.ok !== false && data?.data ? data.data : (data?.data || data || null));
        }
      } catch {
        if (!cancelled) setAvailability(null);
      } finally {
        if (!cancelled) setAvailLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [selectedVenueMeta?.vbProductId, availMonth, excludeAvailOrderKey]);

  const focusSlot = form.timeSlots[focusSlotIdx] || form.timeSlots[0] || null;
  const focusDate = focusSlot?.date || '';

  useEffect(() => {
    if (focusDate && focusDate.length >= 7) {
      const m = focusDate.slice(0, 7);
      if (m !== availMonth) setAvailMonth(m);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [focusDate]);

  const dayAvailBar = useMemo(() => {
    if (!venueNeedsBooking || !focusDate) return { slots: [], interval: 30 };
    return buildDayAvailabilitySlots(availability, focusDate);
  }, [venueNeedsBooking, focusDate, availability]);

  /** 編輯中既有起迄：將自身訂單佔用格視為可用，才能帶入／保留原時間 */
  const daySlotsForPick = useMemo(() => {
    const slots = dayAvailBar.slots || [];
    const startHm = focusSlot?.time || '';
    const endHm = focusSlot?.endTime || '';
    const startM = parseHmToMinutes(startHm);
    const endM = parseHmToMinutes(endHm);
    if (startM == null) return slots;
    let endAbs = endM != null
      ? normalizeEndMinutesAfterStart(startM, endM)
      : startM + (dayAvailBar.interval || 30);
    if (!Number.isFinite(endAbs)) endAbs = startM + (dayAvailBar.interval || 30);
    const dayCap = 24 * 60;
    return slots.map((s) => {
      const m = parseHmToMinutes(s.time);
      if (m != null && m >= startM && m < Math.min(endAbs, dayCap)) {
        return { ...s, available: true, unavailableReason: undefined };
      }
      return s;
    });
  }, [dayAvailBar.slots, dayAvailBar.interval, focusSlot?.time, focusSlot?.endTime]);

  const daySlotsClickable = useMemo(
    () => withRangePickClickable(daySlotsForPick, focusSlot?.time || ''),
    [daySlotsForPick, focusSlot?.time],
  );

  const nextDaySlots = useMemo(() => {
    if (!venueNeedsBooking || !focusDate) return [];
    const d = new Date(`${focusDate}T12:00:00`);
    if (Number.isNaN(d.getTime())) return [];
    d.setDate(d.getDate() + 1);
    const ymd = `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
    return buildDayAvailabilitySlots(availability, ymd).slots || [];
  }, [venueNeedsBooking, focusDate, availability]);

  const startAllowed = useMemo(() => {
    if (!venueNeedsBooking) return null;
    // 可用性尚未載入時不要限制，否則編輯帶入的時間會被下拉清空顯示
    if (availLoading || !(dayAvailBar.slots || []).length) return null;
    let needMin = ORGANIZER_VENUE_MAX_MINUTES;
    if (focusSlot?.time && focusSlot?.endTime && focusSlot?.date) {
      const dur = venueWindowDurationMinutes({
        activityDate: focusSlot.date,
        venueStart: focusSlot.time,
        venueEnd: focusSlot.endTime,
        venueEndDate: focusSlot.venueEndDate || null,
      });
      if (Number.isFinite(dur) && dur > 0) {
        needMin = Math.min(Math.max(dur, 15), ADMIN_VENUE_MAX_MINUTES);
      }
    }
    // 跨多日：空檔條僅涵蓋進場日，改允許當日任一空檔開始
    const multiDay = !!(focusSlot?.venueEndDate
      && focusSlot.venueEndDate !== focusSlot.date);
    const base = multiDay
      ? allowedStartTimes(dayAvailBar.slots)
      : allowedStartTimesForDuration(
        dayAvailBar.slots,
        needMin,
        dayAvailBar.interval,
        nextDaySlots,
      );
    if (!base.length) {
      // fallback：至少允許當日空檔（避免完全鎖死）
      const fallback = allowedStartTimes(daySlotsForPick);
      if (!fallback.length) return null;
      const cur = focusSlot?.time;
      if (cur && !fallback.includes(cur)) return [...fallback, cur].sort();
      return fallback;
    }
    const cur = focusSlot?.time;
    if (cur && !base.includes(cur)) return [...base, cur].sort();
    return base;
  }, [
    venueNeedsBooking, availLoading, dayAvailBar.slots, dayAvailBar.interval, nextDaySlots,
    daySlotsForPick, focusSlot?.time, focusSlot?.endTime, focusSlot?.date, focusSlot?.venueEndDate,
  ]);

  /** 結束：後台不夾 4h；有結束日時開放全日鐘面，否則仍可跨日鐘面 */
  const endAllowed = useMemo(() => {
    if (!venueNeedsBooking) return null;
    const start = focusSlot?.time;
    if (!start) return null;
    if (focusSlot?.venueEndDate && focusSlot.venueEndDate !== focusSlot?.date) {
      return null; // 任意 15 分格
    }
    const vs = parseHmToMinutes(start);
    if (vs == null || !Number.isFinite(vs)) return null;
    const out = [];
    // 同日／鐘面隔日：列出開始後至技術上限內的鐘面
    for (let t = vs + 15; t <= vs + Math.min(ADMIN_VENUE_MAX_MINUTES, 24 * 60); t += 15) {
      out.push(minsToHm(t));
    }
    return out.length ? out : null;
  }, [venueNeedsBooking, focusSlot?.time, focusSlot?.venueEndDate, focusSlot?.date]);

  const monthDayStatus = useMemo(
    () => buildMonthDayStatus(availability, availMonth),
    [availability, availMonth],
  );

  const shiftAvailMonth = (delta) => {
    const [y, m] = (availMonth || '2026-01').split('-').map(Number);
    const dt = new Date(y, m - 1 + delta, 1);
    setAvailMonth(`${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}`);
  };

  const patchFocusSlot = (patch) => {
    setForm((f) => {
      const idx = Math.min(Math.max(0, focusSlotIdx), f.timeSlots.length - 1);
      const slots = [...f.timeSlots];
      slots[idx] = { ...slots[idx], ...patch };
      return { ...f, timeSlots: slots };
    });
  };

  const onAvailSlotClick = (slot) => {
    const cur = form.timeSlots[focusSlotIdx] || form.timeSlots[0];
    if (!cur) return;
    const next = applyRangeSlotClick({
      slots: daySlotsForPick,
      interval: dayAvailBar.interval,
      selectedStart: cur.time,
      selectedEnd: cur.endTime,
      slot,
    });
    const start = next.start || '';
    const end = start ? defaultVenueEndFromStart(start) : '';
    patchFocusSlot({
      time: start,
      endTime: end,
      activityTime: '',
      activityEndTime: '',
    });
  };

  useEffect(() => {
    if (!orgFilterOpen) return;
    const close = (e) => {
      if (orgFilterWrapRef.current && !orgFilterWrapRef.current.contains(e.target)) {
        setOrgFilterOpen(false);
      }
    };
    document.addEventListener('pointerdown', close, true);
    return () => document.removeEventListener('pointerdown', close, true);
  }, [orgFilterOpen]);

  const handleVcSave = async () => {
    if (!vcVenue.trim()) { alert('請選擇場地'); return; }
    setVcSaving(true);
    try {
      const res = await fetch('/api/weekly-activity/venue-color', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          venue: vcVenue.trim(),
          color: normalizeHexColor(vcColor),
          short_name: vcShortName.trim(),
          venue_en: vcVenueEn.trim(),
          short_name_en: vcShortNameEn.trim(),
          code: vcCode.trim(),
          vb_product_id: vcProductId !== '' ? Number(vcProductId) : null,
        }),
      });
      const data = await res.json();
      if (data.success) {
        loadVenueColors();
        setVcVenue('');
        setVcColor('#4ade80');
        setVcShortName('');
        setVcVenueEn('');
        setVcShortNameEn('');
        setVcCode('');
        setVcProductId('');
      } else {
        alert('儲存失敗：' + (data.error || ''));
      }
    } catch (e) {
      console.error(e);
      alert('儲存失敗');
    } finally {
      setVcSaving(false);
    }
  };

  const handleVcDelete = async (id) => {
    if (!window.confirm('確定要刪除此場地顏色？')) return;
    try {
      const res = await fetch(`/api/weekly-activity/venue-color/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) loadVenueColors();
    } catch (e) {
      console.error(e);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const orderedSep = !!form.venue_ordered_separately;
    const venueMeta = venueColors.find((vc) => vc.venue === form.venue);
    const venueProductId = venueMeta?.vbProductId != null ? Number(venueMeta.vbProductId) : null;
    const wantVenueBooking = !orderedSep && !!(form.venueBookingRequested || venueProductId);
    const validSlots = (form.timeSlots || []).filter((s) => {
      if (!s.date || !s.activityTime) return false;
      if (wantVenueBooking && !s.time) return false;
      return true;
    });
    if (validSlots.length === 0 || !form.venue || !form.name) {
      alert(wantVenueBooking
        ? '請填寫：至少一組日期／場地時段／活動時間、場地、名稱'
        : '請填寫：至少一組日期／活動起迄、場地、名稱');
      return;
    }
    if (validSlots.some((s) => !s.activityEndTime)) {
      alert('請填寫活動結束時間');
      return;
    }
    if (wantVenueBooking && venueProductId) {
      const missingEnd = validSlots.some((s) => !s.endTime);
      if (missingEnd) {
        alert('已綁定場地商品，請填寫結束時間以建立場地預約');
        return;
      }
    }
    setSaving(true);
    try {
      const batchGroup = validSlots.length > 1
        ? (form.batch_group || crypto.randomUUID())
        : null;

      const sharedPayload = {
        venue: form.venue,
        name: form.name,
        name_en: form.name_en || '',
        organizer: form.organizer,
        detail: form.detail,
        activity_flow: form.activity_flow || '',
        consumption_type: form.consumption_type,
        status: form.status,
        is_public: form.is_public,
        is_internal: form.is_internal,
        occupies_full_venue: form.occupies_full_venue,
        venueOrderedSeparately: orderedSep,
        beverage_need: form.beverage_need || '',
        handover_notes: form.handover_notes || '',
        internal_brief: form.internal_brief || '',
        equipment_need: form.equipment_need || '',
        registration_link: form.registration_link || '',
        images: form.images,
        organizer_id: form.organizer_id || null,
        batch_group: batchGroup,
      };
      if (wantVenueBooking && venueProductId) {
        sharedPayload.venueBookingRequested = true;
        sharedPayload.venueProductId = venueProductId;
      }

      if (wantVenueBooking && venueProductId) {
        const excludeOrderIds = validSlots
          .map((s) => s.vb_order_id ?? s.vbOrderId ?? s.VB_ORDER_ID)
          .filter((id) => id != null && id !== '' && Number.isFinite(Number(id)))
          .map((id) => Number(id));
        const preRes = await fetch('/api/weekly-activity/precheck-venue', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            venueProductId,
            slots: validSlots.map((s) => ({
              date: s.date,
              time: s.time,
              endTime: s.endTime,
              venue_start_time: s.time,
              venue_end_time: s.endTime,
              venue_end_date: s.venueEndDate || null,
              activity_end_date: s.activityEndDate || null,
            })),
            excludeOrderIds,
          }),
        });
        const preData = await preRes.json();
        if (!preData.success) {
          const errs = Array.isArray(preData.slotErrors) ? preData.slotErrors : [];
          const lines = errs.length
            ? errs.map((e) => {
              const range = [e.date, e.time, e.endTime].filter(Boolean).join(' ');
              return `${range}: ${e.error || '時段不可預約'}`;
            })
            : [preData.error || '場地時段預檢失敗'];
          alert(`場地預檢未通過：\n${lines.join('\n')}`);
          return;
        }
      }

      let okCount = 0;
      let failCount = 0;
      const currentSlotIds = new Set(validSlots.map((s) => s.id).filter(Boolean));
      const originalIds = form._originalSlotIds || [];
      const removedIds = originalIds.filter((id) => !currentSlotIds.has(id));

      const saveOneSlot = async (slot, forceVenueBooking = false) => {
        const isExisting = slot.id != null;
        const url = isExisting
          ? `/api/weekly-activity/${slot.id}`
          : '/api/weekly-activity';
        const method = isExisting ? 'PUT' : 'POST';
        const body = {
          ...sharedPayload,
          activity_date: slot.date,
          venue_start_time: wantVenueBooking ? (slot.time || '') : '',
          venue_end_time: wantVenueBooking ? (slot.endTime || '') : '',
          venue_end_date: wantVenueBooking
            ? (slot.venueEndDate && slot.venueEndDate !== slot.date ? slot.venueEndDate : null)
            : null,
          activity_time: slot.activityTime || slot.time || '',
          end_time: slot.activityEndTime || '',
          activity_end_date: slot.activityEndDate && slot.activityEndDate !== slot.date
            ? slot.activityEndDate
            : null,
        };
        if (forceVenueBooking) body.forceVenueBooking = true;
        const res = await fetch(url, {
          method,
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        });
        const data = await res.json();
        if (!data.success && data.conflict && !forceVenueBooking) {
          const msg = data.error || '場地時段衝突';
          if (window.confirm(`${msg}\n\n是否仍要強制預約？`)) {
            return saveOneSlot(slot, true);
          }
        }
        return data;
      };

      for (const slot of validSlots) {
        try {
          const data = await saveOneSlot(slot);
          if (data.success) okCount++;
          else {
            failCount++;
            const errMsg = data.error || data.message || '未知錯誤';
            if (failCount === 1) {
              alert(`儲存失敗：${errMsg}`);
            }
          }
        } catch (err) {
          failCount++;
          if (failCount === 1) {
            alert(`儲存失敗：${err?.message || '網路錯誤'}`);
          }
        }
      }

      for (const id of removedIds) {
        try {
          const res = await fetch(`/api/weekly-activity/${id}`, { method: 'DELETE' });
          const data = await res.json();
          if (data.success) okCount++;
          else failCount++;
        } catch {
          failCount++;
        }
      }

      if (okCount > 0) {
        setForm(emptyForm());
        setEditingId(null);
        setDetachSingle(false);
        setCopyMode(null);
        loadList();
        loadFieldSuggestions();
      }

      if (failCount === 0) {
        const verb = editingId ? '更新' : '新增';
        const total = validSlots.length + removedIds.length;
        alert(total === 1 ? `已${verb}` : `已成功處理 ${okCount} 筆`);
      } else if (okCount > 0) {
        alert(`成功 ${okCount} 筆，失敗 ${failCount} 筆`);
      }
    } catch (e) {
      console.error(e);
      alert('儲存失敗');
    } finally {
      setSaving(false);
    }
  };

  const buildFormFromItems = (items, batchGroup, fallbackItem) => {
    const base = fallbackItem || items[0] || {};
    const slots = items.map((r) => {
      const slot = slotFromApiRow(r, { id: r.id });
      const oid = slot.vb_order_id ?? r.vb_order_id ?? r.vbOrderId ?? r.VB_ORDER_ID;
      return {
        ...slot,
        vb_order_id: oid != null && oid !== '' && Number.isFinite(Number(oid)) ? Number(oid) : null,
      };
    });
    const ref = items[0] || base;
    const venueName = ref.venue || '';
    const venueMeta = venueColors.find((vc) => vc.venue === venueName);
    return {
      timeSlots: slots.length > 0 ? slots : [slotFromApiRow(base, { id: base.id })],
      venue: venueName,
      name: ref.name || '',
      name_en: ref.name_en || ref.nameEn || '',
      organizer: ref.organizer || '',
      detail: ref.detail || '',
      activity_flow: ref.activity_flow || ref.activityFlow || '',
      consumption_type: ref.consumption_type || '',
      status: ref.status || 'published',
      is_public: ref.is_public !== undefined ? !!ref.is_public : true,
      is_internal: ref.is_internal !== undefined ? !!ref.is_internal : true,
      occupies_full_venue: !!ref.occupies_full_venue,
      venue_ordered_separately: !!(ref.venue_ordered_separately ?? ref.venueOrderedSeparately),
      beverage_need: ref.beverage_need || '',
      handover_notes: ref.handover_notes || '',
      internal_brief: ref.internal_brief || '',
      equipment_need: ref.equipment_need || '',
      registration_link: ref.registration_link || ref.registrationLink || '',
      venueBookingRequested: !!(ref.venue_ordered_separately ?? ref.venueOrderedSeparately)
        ? false
        : !!(venueMeta?.vbProductId),
      images: Array.isArray(ref.images) ? [...ref.images] : [],
      organizer_id: ref.organizer_id ?? null,
      organizer_account_name: ref.organizer_account_name ?? null,
      batch_group: batchGroup || null,
      _originalSlotIds: slots.map((s) => s.id).filter(Boolean),
    };
  };

  const openActivityForm = (items, batchGroup, { detach = false, focusId } = {}) => {
    const focus = items.find((r) => r.id === focusId) || items[0];
    const formItems = detach ? [focus] : items;
    setForm(buildFormFromItems(formItems, detach ? null : batchGroup, focus));
    setEditingId(focus?.id ?? null);
    setDetachSingle(!!detach);
    setCopyMode(null);
    setMobileShowForm(true);
    scrollToTop();
  };

  const handleEdit = async (item) => {
    if (item.batch_group) {
      try {
        const res = await fetch(`/api/weekly-activity/batch/${item.batch_group}`);
        const data = await res.json();
        if (data.success && Array.isArray(data.list) && data.list.length > 1) {
          setBatchEditChoice({ item, list: data.list });
          return;
        }
        if (data.success && Array.isArray(data.list) && data.list.length === 1) {
          openActivityForm(data.list, item.batch_group, { focusId: item.id });
          return;
        }
      } catch (e) {
        console.error('[handleEdit] batch load failed, falling back to single:', e);
      }
    }

    openActivityForm([item], null, { focusId: item.id });
  };

  // 滾動到頁頂（相容 AdminLayout 的 overflow 容器）
  const scrollToTop = () => {
    // 嘗試找到最近的可滾動父容器
    const scrollParent = document.querySelector('[data-admin-content]') || document.scrollingElement || document.documentElement;
    if (scrollParent) {
      scrollParent.scrollTo({ top: 0, behavior: 'smooth' });
    }
    // 同時嘗試 window（備用）
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // 複製活動：內容全拷、日期清空、鐘點預填；視同新增
  const applyCopyToForm = (item) => {
    const copyVenue = item.venue || '';
    const copyVenueMeta = venueColors.find((vc) => vc.venue === copyVenue);
    const copiedSlot = {
      ...slotFromApiRow(item, { id: null }),
      date: '',
      id: null,
      vb_order_id: null,
    };
    setForm({
      timeSlots: [copiedSlot],
      venue: copyVenue,
      name: item.name || '',
      name_en: item.name_en || item.nameEn || '',
      organizer: item.organizer || '',
      detail: item.detail || '',
      activity_flow: item.activity_flow || item.activityFlow || '',
      consumption_type: item.consumption_type || '',
      status: 'draft',
      is_public: false,
      is_internal: false,
      occupies_full_venue: !!item.occupies_full_venue,
      venue_ordered_separately: !!(item.venue_ordered_separately ?? item.venueOrderedSeparately),
      beverage_need: item.beverage_need || '',
      handover_notes: item.handover_notes || '',
      internal_brief: item.internal_brief || '',
      equipment_need: item.equipment_need || '',
      registration_link: item.registration_link || item.registrationLink || '',
      venueBookingRequested: !!(item.venue_ordered_separately ?? item.venueOrderedSeparately)
        ? false
        : !!(copyVenueMeta?.vbProductId),
      images: Array.isArray(item.images) ? [...item.images] : [],
      organizer_id: null,
      organizer_account_name: null,
      batch_group: null,
      _originalSlotIds: [],
    });
    setEditingId(null);
    setDetachSingle(false);
    setCopyMode({ sourceName: item.name || `活動 #${item.id || ''}` });
    setMobileShowForm(true);
    scrollToTop();
  };

  const handleCopy = async (item) => {
    let src = item;
    const hasImages = Array.isArray(item?.images) && item.images.length > 0;
    if (!hasImages && item?.id) {
      try {
        const res = await fetch(`/api/weekly-activity/${item.id}`);
        const data = await res.json();
        const detail = data?.activity || data?.data || null;
        if (data?.success && detail) {
          src = { ...item, ...detail, images: detail.images || item.images || [] };
        }
      } catch { /* 仍用列表列 */ }
    }
    applyCopyToForm(src);
  };

  const copyActivityLink = useCallback((activity) => {
    if (!activity?.id) return;
    const fullUrl = `${window.location.origin}/weekly-activity/${activity.id}`;
    navigator.clipboard.writeText(fullUrl).then(() => {
      toastMessage('已複製活動連結');
    }).catch(() => {
      alert('複製失敗，請手動複製：' + fullUrl);
    });
  }, []);

  const handleDelete = async (id) => {
    if (!window.confirm('確定要刪除此活動？')) return;
    try {
      const res = await fetch(`/api/weekly-activity/${id}`, { method: 'DELETE' });
      const data = await res.json();
      if (data.success) {
        if (editingId === id) {
          setForm(emptyForm());
          setEditingId(null);
          setDetachSingle(false);
          setCopyMode(null);
        }
        loadList();
        alert('已刪除');
      } else {
        alert('刪除失敗：' + (data.error || '未知錯誤'));
      }
    } catch (e) {
      console.error(e);
      alert('刪除失敗');
    }
  };

  /** 主辦活動不可硬刪：改取消以留紀錄 */
  const handleCancelOrganizerActivity = async (row) => {
    const id = row?.id ?? row;
    if (!window.confirm('確定取消此主辦活動？將下架並釋放場地，紀錄會保留。')) return;
    const note = window.prompt('取消備註（可留空）', '管理員取消主辦活動') || '管理員取消主辦活動';
    try {
      const res = await fetch(`/api/vb/admin/organizer-pending/${id}/cancel`, {
        method: 'POST',
        headers: adminAuthHeaders(),
        body: JSON.stringify({ note: String(note).trim().slice(0, 500) }),
      });
      const data = await res.json();
      if (data?.ok) {
        if (editingId === id) {
          setForm(emptyForm());
          setEditingId(null);
          setDetachSingle(false);
          setCopyMode(null);
        }
        loadList();
        refreshPending();
        alert('已取消');
      } else {
        alert('取消失敗：' + (data?.error || '未知錯誤'));
      }
    } catch (e) {
      console.error(e);
      alert('取消失敗');
    }
  };

  const addImageUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      toastMessage('請選擇圖片檔案', 'error');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      toastMessage('圖片大小不能超過 10MB', 'error');
      return;
    }
    setUploading(true);
    try {
      const base64 = await fileToBase64(file);
      const res = await fetch('/api/weekly-activity/upload-image', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ file: base64 }),
      });
      const data = await res.json();
      if (data.success) {
        setForm((f) => ({ ...f, images: [...(f.images || []), data.imageUrl] }));
      } else {
        toastMessage('上傳失敗：' + (data.error || '未知錯誤'), 'error');
      }
    } catch (err) {
      console.error(err);
      toastMessage('上傳失敗', 'error');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const removeImage = (index) => {
    setForm((f) => ({
      ...f,
      images: (f.images || []).filter((_, i) => i !== index),
    }));
  };

  // ===== 產出週活動圖 =====

  // 計算選取日期所在週的範圍 (週一~週日)
  const computedWeekRange = useMemo(() => {
    if (!weekPickerDate) return '';
    const sel = new Date(weekPickerDate + 'T12:00:00');
    const dow = sel.getDay();
    const diffMon = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(sel);
    mon.setDate(sel.getDate() + diffMon);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    return `${mon.getMonth() + 1}/${mon.getDate()} (一) ~ ${sun.getMonth() + 1}/${sun.getDate()} (日)`;
  }, [weekPickerDate]);

  const generateWeeklyImage = async () => {
    setGenerating(true);
    try {
      await document.fonts.ready;

      const sel = new Date(weekPickerDate + 'T12:00:00');
      const dow = sel.getDay();
      const diffMon = dow === 0 ? -6 : 1 - dow;
      const monday = new Date(sel);
      monday.setDate(sel.getDate() + diffMon);
      const mondayStr = dateToYmd(monday);

      const res = await fetch(`/api/weekly-activity?weekStart=${mondayStr}`);
      const data = await res.json();
      const acts = data.success ? data.activities || [] : [];

      const byDate = {};
      acts.forEach((a) => {
        const d = a.activity_date;
        if (!byDate[d]) byDate[d] = [];
        byDate[d].push(a);
      });

      const dates = Array.from({ length: 7 }, (_, i) => {
        const dt = new Date(monday);
        dt.setDate(monday.getDate() + i);
        return dt;
      });

      const canvas = canvasRef.current;
      const W = 1080;
      const CX = 50;
      const CY = 260;
      const CW = 980;
      const BOTTOM_MARGIN = CX;
      /** 標題區～第一列之間 + 列表下緣留白（與舊版 CH-123 一致） */
      const LIST_HEADER = 123;
      const ROW_TOP = CY + 108;
      /** 內容偏少時，列表區至少此高度（約等於 1920 高畫布時之列表高），七日均分 */
      const MIN_CANVAS_H = 1920;
      const minListBodyH = MIN_CANVAS_H - CY - BOTTOM_MARGIN - LIST_HEADER;
      const CR = 28;
      /** 每日區塊內容最小高度（含左欄日期），避免無活動日過矮壓到日期字 */
      const MIN_DAY_BLOCK_INNER_H = 135;
      const DATE_NUM_PX = 95;
      const DATE_WEEK_PX = 28;
      const DAY_EN = ['Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat', 'Sun'];
      const LEFT_COL = 195;
      const actX = CX + LEFT_COL + 10;
      const nameMaxW = CX + CW - actX - 30;
      const NAME_LINE_H = 49;
      const INFO_LINE_H = 41;
      const ENTRY_GAP = 14;
      const DAY_PAD = 16;

      const clampInnerMin = (h) => Math.max(Number(h) || 0, MIN_DAY_BLOCK_INNER_H);

      canvas.width = W;
      canvas.height = 2400;
      const ctx = canvas.getContext('2d');

      const wrapText2Lines = (text, maxW) => {
        if (!text) return ['', ''];
        ctx.font = 'bold 42px "Microsoft JhengHei", sans-serif';
        if (ctx.measureText(text).width <= maxW) return [text, ''];
        let line1 = '';
        let line2 = '';
        for (let ci = 0; ci < text.length; ci += 1) {
          const test = line1 + text[ci];
          if (ctx.measureText(test).width > maxW) {
            line2 = text.slice(ci);
            let truncated = false;
            while (ctx.measureText(line2).width > maxW && line2.length > 1) {
              line2 = line2.slice(0, -1);
              truncated = true;
            }
            if (truncated) line2 += '…';
            break;
          }
          line1 += text[ci];
        }
        return [line1, line2];
      };

      const innerHeightForEntries = (entries) => {
        if (!entries.length) return 51;
        let h = DAY_PAD * 2;
        for (let i = 0; i < entries.length; i += 1) {
          if (i > 0) h += ENTRY_GAP;
          h += entries[i].entryH;
        }
        return h;
      };

      const dayLayouts = dates.map((date, dayIndex) => {
        const ymd = dateToYmd(date);
        const dayActs = byDate[ymd] || [];
        const entries = [];
        for (let ai = 0; ai < dayActs.length; ai += 1) {
          const a = dayActs[ai];
          const [line1, line2] = wrapText2Lines(a.name || '', nameMaxW);
          const nameLines = line2 ? 2 : 1;
          const entryH = nameLines * NAME_LINE_H + INFO_LINE_H;
          entries.push({ a, line1, line2, nameLines, entryH });
        }
        const rawInner = dayActs.length === 0 ? 51 + DAY_PAD * 2 : innerHeightForEntries(entries);
        const innerH = clampInnerMin(rawInner);
        return {
          dayIndex,
          date,
          dayActs,
          entries,
          innerH,
          kind: dayActs.length === 0 ? 'empty' : 'day',
        };
      });

      const baseHeights = dayLayouts.map((d) => d.innerH);
      const totalBaseH = baseHeights.reduce((s, h) => s + h, 0);
      const listBodyH = Math.max(totalBaseH, minListBodyH);
      const extraPerDay = (listBodyH - totalBaseH) / 7;
      const CH = LIST_HEADER + listBodyH;
      const canvasH = CY + CH + BOTTOM_MARGIN;

      canvas.height = canvasH;

      const bg = new Image();
      await new Promise((resolve, reject) => {
        bg.onload = resolve;
        bg.onerror = reject;
        bg.src = cardBackImg;
      });

      const month = monday.getMonth() + 1;
      const MONTH_SHORT = ['', 'Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const sunday = dates[6];
      const titleBase = `${month}月 ${MONTH_SHORT[month]}.  ${monday.getMonth() + 1}/${monday.getDate()}  ~  ${sunday.getMonth() + 1}/${sunday.getDate()} 活動`;

      ctx.clearRect(0, 0, W, canvasH);
      ctx.drawImage(bg, 0, 0, W, canvasH);
      ctx.save();
      ctx.shadowColor = 'rgba(0,0,0,0.3)';
      ctx.shadowBlur = 40;
      ctx.shadowOffsetY = 6;
      ctx.fillStyle = 'rgba(255, 251, 243, 0.95)';
      drawRoundRect(ctx, CX, CY, CW, CH, CR);
      ctx.fill();
      ctx.restore();

      const titleY = CY + 68;
      const titleMainFont = 'bold 57px MarcellusSC, Georgia, "Times New Roman", serif';
      ctx.textBaseline = 'alphabetic';
      ctx.textAlign = 'center';
      ctx.fillStyle = '#b22222';
      ctx.font = titleMainFont;
      ctx.fillText(titleBase, W / 2, titleY);

      ctx.strokeStyle = '#e0d8c8';
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(CX + 60, CY + 92);
      ctx.lineTo(CX + CW - 60, CY + 92);
      ctx.stroke();

      let cursorY = ROW_TOP;
      dayLayouts.forEach((seg, i) => {
        const rowH = baseHeights[i] + extraPerDay;
        const ry = cursorY;

        if (i > 0) {
          ctx.strokeStyle = '#c0b5a0';
          ctx.lineWidth = 3;
          ctx.beginPath();
          ctx.moveTo(CX + 20, ry);
          ctx.lineTo(CX + CW - 20, ry);
          ctx.stroke();
        }

        const blockTop = ry;
        const dateCenterY = blockTop + rowH / 2;
        const dateCenterX = CX + LEFT_COL / 2;

        ctx.textBaseline = 'middle';
        ctx.textAlign = 'center';
        ctx.fillStyle = '#b22222';
        const dateNumStr = String(seg.date.getDate());
        const dateNumFont = `900 ${DATE_NUM_PX}px MarcellusSC, Georgia, "Arial Black", serif`;
        ctx.save();
        ctx.font = dateNumFont;
        ctx.lineWidth = 1.1;
        ctx.strokeStyle = '#b22222';
        ctx.lineJoin = 'round';
        ctx.strokeText(dateNumStr, dateCenterX, dateCenterY - 18);
        ctx.fillText(dateNumStr, dateCenterX, dateCenterY - 18);
        ctx.restore();
        ctx.fillStyle = '#999';
        ctx.font = `${DATE_WEEK_PX}px "Microsoft JhengHei", sans-serif`;
        ctx.fillText(DAY_EN[seg.dayIndex], dateCenterX, dateCenterY + 42);
        ctx.textBaseline = 'alphabetic';

        ctx.textAlign = 'left';
        if (seg.kind === 'empty') {
          ctx.fillStyle = '#bbb';
          ctx.font = '35px "Microsoft JhengHei", sans-serif';
          ctx.fillText('No Event 沒活動', actX, blockTop + rowH / 2 + 12);
        } else {
          const contentH = seg.entries.reduce((sum, e, idx) => {
            let s = e.entryH;
            if (idx > 0) s += ENTRY_GAP;
            return sum + s;
          }, 0);
          let entryY = blockTop + (rowH - contentH) / 2;

          for (let ai = 0; ai < seg.entries.length; ai += 1) {
            const { a, line1, line2, entryH } = seg.entries[ai];
            const vc = a.venue_color || '#4ade80';
            const venueShort = a.venue_short_name || a.venue || '';
            if (ai > 0) entryY += ENTRY_GAP;

            ctx.fillStyle = '#222';
            ctx.font = 'bold 42px "Microsoft JhengHei", sans-serif';
            ctx.fillText(line1, actX, entryY + NAME_LINE_H * 0.78);
            if (line2) {
              ctx.fillText(line2, actX, entryY + NAME_LINE_H * 0.78 + NAME_LINE_H);
            }

            const nameH = (line2 ? 2 : 1) * NAME_LINE_H;
            const infoY = entryY + nameH + INFO_LINE_H * 0.78;
            ctx.fillStyle = '#c23030';
            ctx.font = 'bold 30px "Microsoft JhengHei", sans-serif';
            const timeStr = formatActivityTimeRange(a.activity_time, a.end_time) || a.activity_time || '';
            ctx.fillText(timeStr, actX, infoY);
            const timeW = ctx.measureText(timeStr).width;
            const venueX = actX + timeW + 28;
            ctx.fillStyle = vc;
            ctx.beginPath();
            ctx.arc(venueX + 8, infoY - 7, 7, 0, Math.PI * 2);
            ctx.fill();
            ctx.fillStyle = '#777';
            ctx.font = '30px "Microsoft JhengHei", sans-serif';
            ctx.fillText(venueShort, venueX + 22, infoY);

            entryY += entryH;
          }
        }

        cursorY += rowH;
      });

      setPreviewPages([canvas.toDataURL('image/png')]);
    } catch (err) {
      console.error('generateWeeklyImage error:', err);
      alert('產生圖片失敗：' + err.message);
    } finally {
      setGenerating(false);
    }
  };

  const downloadImage = () => {
    if (!previewPages.length) return;
    const sel = new Date(weekPickerDate + 'T12:00:00');
    const dow = sel.getDay();
    const diffMon = dow === 0 ? -6 : 1 - dow;
    const mon = new Date(sel);
    mon.setDate(sel.getDate() + diffMon);
    const sun = new Date(mon);
    sun.setDate(mon.getDate() + 6);
    const base = `weekly-activity-${dateToYmd(mon)}-to-${dateToYmd(sun)}`;
    const link = document.createElement('a');
    link.download = `${base}.png`;
    link.href = previewPages[0];
    link.click();
  };

  const uniqueOrganizers = useMemo(() => {
    const s = new Set();
    list.forEach((r) => {
      const o = (r.organizer || '').trim();
      if (o) s.add(o);
    });
    return [...s].sort((a, b) => a.localeCompare(b, 'zh-Hant'));
  }, [list]);

  const filteredList = useMemo(() => {
    let start = filterDateStart;
    let end = filterDateEnd;
    if (start && end && start > end) {
      const t = start;
      start = end;
      end = t;
    }
    const orgSet = new Set(filterOrganizerSet);
    const useOrg = orgSet.size > 0;
    return list.filter((r) => {
      const d = r.activity_date || '';
      if (start && d < start) return false;
      if (end && d > end) return false;
      if (useOrg) {
        const o = (r.organizer || '').trim();
        if (!orgSet.has(o)) return false;
      }
      if (filterUnboundOrganizer && r.organizer_id != null) return false;
      return true;
    });
  }, [list, filterDateStart, filterDateEnd, filterOrganizerSet, filterUnboundOrganizer]);

  const changeMonth = (delta) => {
    const anchor = new Date((filterDateStart || todayYmd()) + 'T12:00:00');
    if (Number.isNaN(anchor.getTime())) return;
    anchor.setMonth(anchor.getMonth() + delta);
    const y = anchor.getFullYear();
    const m0 = anchor.getMonth();
    setFilterCalView({ y, m0 });
    const { start, end } = monthBoundsYmd(y, m0);
    setFilterDateStart(start);
    setFilterDateEnd(end);
  };

  const applyCurrentMonth = () => {
    const d = new Date();
    const y = d.getFullYear();
    const m0 = d.getMonth();
    setFilterCalView({ y, m0 });
    const { start, end } = monthBoundsYmd(y, m0);
    setFilterDateStart(start);
    setFilterDateEnd(end);
  };

  const shiftFilterCalView = (delta) => {
    setFilterCalView(({ y, m0 }) => {
      const d = new Date(y, m0 + delta, 1);
      return { y: d.getFullYear(), m0: d.getMonth() };
    });
  };

  const filterCalCells = useMemo(
    () => buildCalendarGrid(filterCalView.y, filterCalView.m0),
    [filterCalView.y, filterCalView.m0],
  );

  const displayRangeLoHi = useMemo(() => {
    const pick = rangeDragPreview;
    const lo = pick ? pick.start : filterDateStart;
    const hi = pick ? pick.end : filterDateEnd;
    if (!lo || !hi) return { lo: '', hi: '' };
    return lo <= hi ? { lo, hi } : { lo: hi, hi: lo };
  }, [rangeDragPreview, filterDateStart, filterDateEnd]);

  const beginRangePointerDrag = (ymd, e) => {
    if (e.pointerType === 'mouse' && e.button !== 0) return;
    e.preventDefault();
    rangeDragRef.current = { anchor: ymd, hover: ymd };
    setRangeDragPreview({ start: ymd, end: ymd });

    const onMove = (ev) => {
      if (!rangeDragRef.current) return;
      const el = document.elementFromPoint(ev.clientX, ev.clientY);
      const cell = el?.closest?.('[data-range-ymd]');
      const next = cell?.getAttribute('data-range-ymd');
      if (!next) return;
      rangeDragRef.current.hover = next;
      const { anchor } = rangeDragRef.current;
      const lo = anchor < next ? anchor : next;
      const hi = anchor < next ? next : anchor;
      setRangeDragPreview({ start: lo, end: hi });
    };

    const endDrag = () => {
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', endDrag);
      window.removeEventListener('pointercancel', endDrag);
      if (rangeDragRef.current) {
        const { anchor, hover } = rangeDragRef.current;
        const h = hover || anchor;
        const lo = anchor < h ? anchor : h;
        const hi = anchor < h ? h : anchor;
        setFilterDateStart(lo);
        setFilterDateEnd(hi);
      }
      rangeDragRef.current = null;
      setRangeDragPreview(null);
    };

    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
  };

  const filterRangeLabel = useMemo(() => {
    if (isFullCalendarMonthRange(filterDateStart, filterDateEnd)) {
      const [y, m] = filterDateStart.split('-').map(Number);
      return `${y} 年 ${m} 月`;
    }
    if (filterDateStart && filterDateEnd) return `${filterDateStart} ~ ${filterDateEnd}`;
    if (filterDateStart) return `${filterDateStart} 起`;
    if (filterDateEnd) return `至 ${filterDateEnd}`;
    return '全部日期';
  }, [filterDateStart, filterDateEnd]);

  const toggleOrganizerFilter = (name) => {
    setFilterOrganizerSet((prev) => {
      const has = prev.includes(name);
      if (has) return prev.filter((x) => x !== name);
      return [...prev, name];
    });
  };

  const clearOrganizerFilters = () => setFilterOrganizerSet([]);

  const containerStyle = {
    maxWidth: 1200,
    width: '100%',
    margin: '0 auto',
    padding: 16,
    paddingBottom: 32,
    boxSizing: 'border-box',
    fontFamily: 'system-ui, "Microsoft JhengHei", sans-serif',
    minHeight: 'min-content',
    overflowX: 'hidden',
    minWidth: 0,
  };

  const tableWrap = {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
    maxWidth: '100%',
    marginBottom: 24,
    border: '1px solid #ddd',
    borderRadius: 8,
    background: '#fff',
  };

  const tableStyle = {
    width: '100%',
    borderCollapse: 'collapse',
    fontSize: 14,
  };

  const thTdStyle = {
    borderBottom: '2px solid #d0d5dd',
    padding: '10px 12px',
    textAlign: 'left',
    verticalAlign: 'top',
    wordBreak: 'break-word',
    overflowWrap: 'anywhere',
  };

  const formSectionStyle = {
    background: '#f8f9fa',
    border: '1px solid #dee2e6',
    borderRadius: 12,
    padding: 20,
    marginBottom: 16,
    maxWidth: '100%',
    minWidth: 0,
    overflowX: 'hidden',
    boxSizing: 'border-box',
  };

  const formGridStyle = {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '16px 24px',
  };

  const formSplitStyle = {
    display: 'grid',
    gridTemplateColumns: waNarrow ? 'minmax(0, 1fr)' : 'minmax(0, 1fr) minmax(240px, 360px)',
    gap: 20,
    alignItems: 'start',
    minWidth: 0,
  };

  const formFullWidth = { gridColumn: '1 / -1' };

  const inputStyle = {
    width: '100%',
    padding: '8px 12px',
    borderRadius: 6,
    border: '1px solid #ccc',
    fontSize: 14,
    marginBottom: 0,
    boxSizing: 'border-box',
  };

  const textareaStyle = {
    ...inputStyle,
    minHeight: 80,
    resize: 'vertical',
  };

  const btnStyle = {
    padding: '8px 16px',
    borderRadius: 8,
    border: '1px solid #ccc',
    background: '#fff',
    cursor: 'pointer',
    fontSize: 14,
    marginRight: 8,
  };

  const btnPrimary = { ...btnStyle, background: '#7b4f2f', color: '#fff', borderColor: '#7b4f2f' };
  const btnDanger = { ...btnStyle, background: '#dc3545', color: '#fff', borderColor: '#dc3545' };
  const btnSuccess = { ...btnStyle, background: '#28a745', color: '#fff', borderColor: '#28a745' };

  return (
    <div style={containerStyle}>
      {!waNarrow && (
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 16 }}>
          <h1 style={{ marginTop: 0, marginBottom: 0, fontSize: 22 }}>週活動管理</h1>
          <a
            href="/weekly-activity"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '8px 16px',
              background: '#1677ff',
              color: '#fff',
              borderRadius: 6,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500,
            }}
          >
            📅 前往月曆
          </a>
        </div>
      )}

      {waNarrow ? (
        <select
          value={adminTab}
          onChange={(e) => setAdminTab(e.target.value)}
          style={{ width: '100%', padding: '10px 12px', fontSize: 16, fontWeight: 700, marginBottom: 12, borderRadius: 8, border: '1px solid #ccc' }}
        >
          <option value="activities">活動管理</option>
          <option value="card_print">卡片列印</option>
          <option value="organizer_pending">主辦送審{pendingActionCount ? `（${pendingActionCount}）` : ''}</option>
          <option value="organizer_accounts">主辦帳號</option>
          <option value="organizer_review_notify">主辦審核通知</option>
          <option value="organizer_finance">主辦財務</option>
        </select>
      ) : (
      <div style={{ display: 'flex', gap: 8, marginBottom: 16, flexWrap: 'wrap' }}>
        <button
          type="button"
          style={{
            ...btnStyle,
            ...(adminTab === 'activities' ? { ...btnPrimary, fontWeight: 700 } : {}),
          }}
          onClick={() => setAdminTab('activities')}
        >
          活動管理
        </button>
        <button
          type="button"
          style={{
            ...btnStyle,
            ...(adminTab === 'card_print' ? { ...btnPrimary, fontWeight: 700 } : {}),
          }}
          onClick={() => setAdminTab('card_print')}
        >
          卡片列印
        </button>
        <button
          type="button"
          style={{
            ...btnStyle,
            ...(adminTab === 'organizer_pending' ? { ...btnPrimary, fontWeight: 700 } : {}),
          }}
          onClick={() => setAdminTab('organizer_pending')}
        >
          主辦送審
          {pendingActionCount > 0 && (
            <span
              title={`${pendingActionCount} 筆待確認`}
              style={{
                display: 'inline-block',
                width: 8,
                height: 8,
                marginLeft: 6,
                borderRadius: '50%',
                background: '#ff4d4f',
                verticalAlign: 'middle',
              }}
            />
          )}
        </button>
        <button
          type="button"
          style={{
            ...btnStyle,
            ...(adminTab === 'organizer_accounts' ? { ...btnPrimary, fontWeight: 700 } : {}),
          }}
          onClick={() => setAdminTab('organizer_accounts')}
        >
          主辦帳號
        </button>
        <button
          type="button"
          style={{
            ...btnStyle,
            ...(adminTab === 'organizer_review_notify' ? { ...btnPrimary, fontWeight: 700 } : {}),
          }}
          onClick={() => setAdminTab('organizer_review_notify')}
        >
          主辦審核通知
        </button>
        <button
          type="button"
          style={{
            ...btnStyle,
            ...(adminTab === 'organizer_finance' ? { ...btnPrimary, fontWeight: 700 } : {}),
          }}
          onClick={() => setAdminTab('organizer_finance')}
        >
          主辦財務
        </button>
      </div>
      )}

      {adminTab === 'card_print' && (
        <WeeklyActivityCardPrintPanel
          activities={list}
          loading={loading}
          btnStyle={btnStyle}
          btnPrimary={btnPrimary}
          waNarrow={waNarrow}
        />
      )}

      {adminTab === 'organizer_pending' && (
        <section style={{ ...formSectionStyle, marginBottom: 24 }}>
          <h2 style={{ marginTop: 0, fontSize: 18 }}>主辦送審（月曆＋商品）</h2>
          <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end', marginBottom: 14 }}>
            <label style={{ fontSize: 13 }}>
              日期起
              <input type="date" value={pendingDateFrom} onChange={(e) => setPendingDateFrom(e.target.value)} style={{ ...inputStyle, display: 'block', width: 150, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 13 }}>
              日期迄
              <input type="date" value={pendingDateTo} onChange={(e) => setPendingDateTo(e.target.value)} style={{ ...inputStyle, display: 'block', width: 150, marginTop: 4 }} />
            </label>
            <label style={{ fontSize: 13 }}>
              主辦
              <select value={pendingOrganizerId} onChange={(e) => setPendingOrganizerId(e.target.value)} style={{ ...inputStyle, display: 'block', width: 180, marginTop: 4 }}>
                <option value="">全部</option>
                {organizerOptions.map((o) => {
                  const oid = o.ID ?? o.id;
                  return (
                    <option key={oid} value={oid}>{o.DISPLAY_NAME || o.displayName || o.LOGIN || o.login || `#${oid}`}</option>
                  );
                })}
              </select>
            </label>
            <label style={{ fontSize: 13 }}>
              場地
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                {pendingVenue && (() => {
                  const meta = venueColors.find((vc) => vc.venue === pendingVenue);
                  return meta?.color ? (
                    <span style={{
                      display: 'inline-block', width: 14, height: 14, borderRadius: 3,
                      background: meta.color, border: '1px solid #ccc', flexShrink: 0,
                    }}
                    />
                  ) : null;
                })()}
                <select value={pendingVenue} onChange={(e) => setPendingVenue(e.target.value)} style={{ ...inputStyle, width: 180, marginBottom: 0 }}>
                  <option value="">全部</option>
                  {venueSelectOptions.map((v) => (
                    <option key={v.venue} value={v.venue}>{formatVenueOptionLabel(v.venue, v.code)}</option>
                  ))}
                </select>
              </div>
            </label>
            <div style={{ fontSize: 13 }}>
              <div style={{ marginBottom: 4 }}>狀態</div>
              <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
                {[
                  { value: 'pending', label: '新活動待審核' },
                  { value: 'rejected', label: '退回中' },
                  { value: 'ended_pass_pending', label: '待發日券' },
                  { value: 'pass_closed', label: '結案' },
                  { value: 'cancelled', label: '已取消' },
                ].map((s) => (
                  <label key={s.value} style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={pendingStatus.includes(s.value)}
                      onChange={(e) => {
                        setPendingStatus((prev) => (
                          e.target.checked
                            ? [...new Set([...prev, s.value])]
                            : prev.filter((x) => x !== s.value)
                        ));
                      }}
                    />
                    {s.label}
                  </label>
                ))}
                <label style={{ display: 'flex', alignItems: 'center', gap: 4, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={pendingResubmitOnly}
                    onChange={(e) => setPendingResubmitOnly(e.target.checked)}
                  />
                  僅退回後再送
                </label>
              </div>
            </div>
            <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, cursor: 'pointer', paddingBottom: 8 }}>
              <input
                type="checkbox"
                checked={pendingIncludeApproved}
                onChange={(e) => setPendingIncludeApproved(e.target.checked)}
              />
              含已核准
            </label>
            <button type="button" style={btnPrimary} onClick={loadPendingCalendar} disabled={pendingLoading}>
              {pendingLoading ? '載入中…' : '套用篩選'}
            </button>
          </div>
          {pendingLoading ? (
            <p style={{ color: '#666' }}>載入中…</p>
          ) : pendingCalendarDisplay.length === 0 ? (
            <p style={{ color: '#888' }}>目前無待審項目</p>
          ) : waNarrow ? (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, minWidth: 0 }}>
              {pendingCalendarDisplay.map((r) => {
                  const kind = r.PENDING_KIND ?? r.pendingKind ?? 'calendar';
                  const id = kind === 'product'
                    ? (r.PRODUCT_ID ?? r.productId ?? r.VB_PRODUCT_ID)
                    : (r.ID ?? r.id);
                  const submitted = String(r.ORGANIZER_SUBMITTED_AT ?? r.organizerSubmittedAt ?? '').replace('T', ' ').slice(0, 19);
                  const pid = r.VB_PRODUCT_ID ?? r.vbProductId ?? r.PRODUCT_ID ?? r.productId;
                  const pname = r.PRODUCT_NAME ?? r.productName ?? r.NAME ?? r.name;
                  const pslug = r.PRODUCT_SLUG ?? r.productSlug ?? r.PRODUCT_SLUG;
                  const st = String(r.STATUS ?? r.status ?? '').toLowerCase();
                  const rejectReason = String(r.ORGANIZER_REJECT_REASON ?? r.organizerRejectReason ?? '').trim();
                  const rejectReasonShort = rejectReason.length > 80 ? `${rejectReason.slice(0, 80)}…` : rejectReason;
                  const hasRevision = !!(r.HAS_PENDING_REVISION ?? r.hasPendingRevision);
                  const passStatus = String(r.PASS_GRANT_STATUS ?? r.passGrantStatus ?? '').trim();
                  const passQty = Number(r.PASS_GRANT_QTY ?? r.passGrantQty) || 0;
                  const isPassClosed = kind === 'pass_closed' || ['issued', 'skipped', 'no_member'].includes(passStatus);
                  const isEndedPass = kind === 'ended_pass';
                  const phase = r.REVIEW_PHASE || r.reviewPhase || '';
                  let statusLabel = '—';
                  if (phase === 'first_pending') statusLabel = '首次待審';
                  else if (phase === 'resubmit_after_reject') statusLabel = '退回後再送';
                  else if (phase === 'revision_pending') statusLabel = '改版待審';
                  else if (phase === 'rejected') statusLabel = '退回中';
                  else if (isEndedPass) statusLabel = '待發日券';
                  else if (isPassClosed) {
                    statusLabel = passStatus === 'issued'
                      ? `結案·已發 ${passQty} 張${(r.PASS_GRANT_ADMIN ?? r.passGrantAdmin) === 'auto' ? '·自動' : ''}`
                      : passStatus === 'no_member'
                        ? '結案·未綁會員'
                        : '結案·不發放';
                  } else if (st === 'cancelled') statusLabel = '已取消';
                  else if (st === 'rejected' || (rejectReason && !(r.ORGANIZER_SUBMITTED_AT || r.organizerSubmittedAt))) statusLabel = '退回中';
                  else if (r.ORGANIZER_APPROVED_AT || r.organizerApprovedAt) statusLabel = '已核准';
                  else if (hasRevision) statusLabel = '改版待審';
                  else if (r.ORGANIZER_SUBMITTED_AT || r.organizerSubmittedAt) statusLabel = '待審核';
                  else if (rejectReason) statusLabel = '退回中';
                  const venueName = r.VENUE ?? r.venue ?? '';
                  const venueMeta = venueColors.find((vc) => vc.venue === venueName);
                  const memberId = r.ORGANIZER_MEMBER_ID ?? r.organizerMemberId;
                  const memberLabel = memberId
                    ? `${r.ORGANIZER_MEMBER_NAME ?? ''} (#${memberId})`.trim()
                    : null;
                  const rowKey = kind === 'product' ? `p-${id}` : isEndedPass ? `pass-${id}` : isPassClosed ? `closed-${id}` : `c-${id}`;
                  const isCancelledPending = st === 'cancelled';
                  const isRejectedPending = !isCancelledPending && statusLabel === '退回中';
                  const isResubmitPending = !isCancelledPending && statusLabel === '退回後再送';
                  const isRevisionPending = !isCancelledPending && statusLabel === '改版待審';
                  const showRejectMeta = phase === 'resubmit_after_reject' || phase === 'rejected' || isRejectedPending || isResubmitPending;
                  const organizerDisplay = r.ORGANIZER_DISPLAY_NAME ?? r.organizerDisplayName
                    ?? r.ORGANIZER_LOGIN ?? r.organizerLogin
                    ?? r.ORGANIZER ?? r.organizer ?? '';
                  const kindLabel = kind === 'product' ? '商品' : (isEndedPass || isPassClosed) ? '日券' : '月曆';
                  return (
                    <div
                      key={rowKey}
                      style={{
                        background: isPassClosed
                          ? '#f3f4f6'
                          : isRejectedPending
                            ? '#fff2f0'
                            : isResubmitPending
                              ? '#fffbe6'
                              : isRevisionPending
                                ? '#e6f7ff'
                                : '#fff',
                        border: '1px solid #dee2e6',
                        borderRadius: 10,
                        padding: 12,
                        minWidth: 0,
                        boxSizing: 'border-box',
                        wordBreak: 'break-word',
                        boxShadow: isCancelledPending
                          ? 'inset 4px 0 0 #8c8c8c'
                          : isRejectedPending
                            ? 'inset 4px 0 0 #ff4d4f'
                            : isResubmitPending
                              ? 'inset 4px 0 0 #faad14'
                              : isRevisionPending
                                ? 'inset 4px 0 0 #1890ff'
                                : undefined,
                      }}
                    >
                      <div style={{ fontSize: 12, color: '#666' }}>{kindLabel} · {statusLabel} · #{id ?? '—'}</div>
                      <div style={{ fontWeight: 700, marginTop: 4 }}>{r.NAME ?? r.name ?? '—'}</div>
                      <div style={{ fontSize: 13, marginTop: 4 }}>
                        {r.ACTIVITY_DATE ?? r.activityDate}{' '}
                        {r.ACTIVITY_TIME ?? r.activityTime}
                        {(r.END_TIME ?? r.endTime) ? ` ~ ${r.END_TIME ?? r.endTime}` : ''}
                      </div>
                      <div style={{ fontSize: 13, color: '#555', marginTop: 4 }}>
                        {formatVenueOptionLabel(venueName, venueMeta?.code)}
                        {showRejectMeta
                          ? (organizerDisplay ? ` · ${organizerDisplay}` : '')
                          : (r.ORGANIZER_LOGIN ?? r.organizerLogin ? ` · ${r.ORGANIZER_LOGIN ?? r.organizerLogin}` : '')}
                      </div>
                      {showRejectMeta && rejectReasonShort ? (
                        <div style={{ fontSize: 12, color: isRejectedPending ? '#cf1322' : '#d48806', marginTop: 4 }} title={rejectReason}>
                          退回原因：{rejectReasonShort}
                        </div>
                      ) : null}
                      {pid ? <div style={{ fontSize: 12, color: '#666', marginTop: 4 }}>商品 #{pid}{pslug ? ` (${pslug})` : ''}{pname ? ` ${pname}` : ''}</div> : null}
                      {submitted ? <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>送審 {submitted}</div> : null}
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 10 }}>
                        {isEndedPass ? (
                          <>
                            {memberId ? <span style={{ fontSize: 12 }}>{memberLabel}</span> : <span style={{ fontSize: 12, color: '#9ca3af' }}>未綁會員</span>}
                            {memberId ? (
                              <button type="button" style={btnPrimary} onClick={() => openPassGrantModal(id, 'issue', memberLabel)}>發放日券</button>
                            ) : null}
                            <button type="button" style={btnStyle} onClick={() => handlePassGrantSkip(id)}>這次不發</button>
                          </>
                        ) : isPassClosed ? (
                          <>
                            <span style={{ fontSize: 12 }}>已發累計 {passQty} 張</span>
                            {memberId ? (
                              <button type="button" style={btnPrimary} onClick={() => openPassGrantModal(id, 'reissue', memberLabel)}>補發日券</button>
                            ) : (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>未綁會員，無法補發</span>
                            )}
                          </>
                        ) : (
                          <>
                            <button type="button" style={btnPrimary} onClick={() => openReview(r)}>審核</button>
                            <button type="button" style={btnDanger} onClick={() => openReject(r)}>退回</button>
                          </>
                        )}
                      </div>
                    </div>
                  );
              })}
            </div>
          ) : (
            <div style={{ overflowX: 'auto', maxWidth: '100%', minWidth: 0 }}>
            <table style={tableStyle}>
              <thead>
                <tr>
                  {['類型', '狀態', 'ID', '日期', '時間', '場地', '名稱', '主辦帳號', '關聯商品', '送審時間', '操作'].map((h) => (
                    <th key={h} style={thTdStyle}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {pendingCalendarDisplay.map((r) => {
                  const kind = r.PENDING_KIND ?? r.pendingKind ?? 'calendar';
                  const id = kind === 'product'
                    ? (r.PRODUCT_ID ?? r.productId ?? r.VB_PRODUCT_ID)
                    : (r.ID ?? r.id);
                  const submitted = String(r.ORGANIZER_SUBMITTED_AT ?? r.organizerSubmittedAt ?? '').replace('T', ' ').slice(0, 19);
                  const pid = r.VB_PRODUCT_ID ?? r.vbProductId ?? r.PRODUCT_ID ?? r.productId;
                  const pname = r.PRODUCT_NAME ?? r.productName ?? r.NAME ?? r.name;
                  const pslug = r.PRODUCT_SLUG ?? r.productSlug ?? r.PRODUCT_SLUG;
                  const st = String(r.STATUS ?? r.status ?? '').toLowerCase();
                  const rejectReason = String(r.ORGANIZER_REJECT_REASON ?? r.organizerRejectReason ?? '').trim();
                  const rejectReasonShort = rejectReason.length > 80 ? `${rejectReason.slice(0, 80)}…` : rejectReason;
                  const hasRevision = !!(r.HAS_PENDING_REVISION ?? r.hasPendingRevision);
                  const passStatus = String(r.PASS_GRANT_STATUS ?? r.passGrantStatus ?? '').trim();
                  const passQty = Number(r.PASS_GRANT_QTY ?? r.passGrantQty) || 0;
                  const isPassClosed = kind === 'pass_closed' || ['issued', 'skipped', 'no_member'].includes(passStatus);
                  const isEndedPass = kind === 'ended_pass';
                  const phase = r.REVIEW_PHASE || r.reviewPhase || '';
                  let statusLabel = '—';
                  if (phase === 'first_pending') statusLabel = '首次待審';
                  else if (phase === 'resubmit_after_reject') statusLabel = '退回後再送';
                  else if (phase === 'revision_pending') statusLabel = '改版待審';
                  else if (phase === 'rejected') statusLabel = '退回中';
                  else if (isEndedPass) statusLabel = '待發日券';
                  else if (isPassClosed) {
                    statusLabel = passStatus === 'issued'
                      ? `結案·已發 ${passQty} 張${(r.PASS_GRANT_ADMIN ?? r.passGrantAdmin) === 'auto' ? '·自動' : ''}`
                      : passStatus === 'no_member'
                        ? '結案·未綁會員'
                        : '結案·不發放';
                  } else if (st === 'cancelled') statusLabel = '已取消';
                  else if (st === 'rejected' || (rejectReason && !(r.ORGANIZER_SUBMITTED_AT || r.organizerSubmittedAt))) statusLabel = '退回中';
                  else if (r.ORGANIZER_APPROVED_AT || r.organizerApprovedAt) statusLabel = '已核准';
                  else if (hasRevision) statusLabel = '改版待審';
                  else if (r.ORGANIZER_SUBMITTED_AT || r.organizerSubmittedAt) statusLabel = '待審核';
                  else if (rejectReason) statusLabel = '退回中';
                  const venueName = r.VENUE ?? r.venue ?? '';
                  const venueMeta = venueColors.find((vc) => vc.venue === venueName);
                  const memberId = r.ORGANIZER_MEMBER_ID ?? r.organizerMemberId;
                  const memberLabel = memberId
                    ? `${r.ORGANIZER_MEMBER_NAME ?? ''} (#${memberId})`.trim()
                    : null;
                  const rowKey = kind === 'product' ? `p-${id}` : isEndedPass ? `pass-${id}` : isPassClosed ? `closed-${id}` : `c-${id}`;
                  const rowStyle = isPassClosed
                    ? { ...thTdStyle, color: '#9ca3af', background: '#f3f4f6' }
                    : thTdStyle;
                  const isCancelledPending = st === 'cancelled';
                  const isRejectedPending = !isCancelledPending && statusLabel === '退回中';
                  const isResubmitPending = !isCancelledPending && statusLabel === '退回後再送';
                  const isRevisionPending = !isCancelledPending && statusLabel === '改版待審';
                  const showRejectMeta = phase === 'resubmit_after_reject' || phase === 'rejected' || isRejectedPending || isResubmitPending;
                  const organizerDisplay = r.ORGANIZER_DISPLAY_NAME ?? r.organizerDisplayName
                    ?? r.ORGANIZER_LOGIN ?? r.organizerLogin
                    ?? r.ORGANIZER ?? r.organizer ?? '';
                  const pendingRowStyle = isPassClosed
                    ? { opacity: 0.85 }
                    : isCancelledPending
                      ? {
                        background: '#f5f5f5',
                        boxShadow: 'inset 4px 0 0 #8c8c8c',
                        outline: '2px solid #bfbfbf',
                        outlineOffset: -2,
                      }
                      : isRejectedPending
                        ? {
                          background: '#fff2f0',
                          boxShadow: 'inset 4px 0 0 #ff4d4f',
                          outline: '2px solid #ffa39e',
                          outlineOffset: -2,
                        }
                        : isResubmitPending
                          ? {
                            background: '#fffbe6',
                            boxShadow: 'inset 4px 0 0 #faad14',
                            outline: '2px solid #ffe58f',
                            outlineOffset: -2,
                          }
                          : isRevisionPending
                            ? {
                              background: '#e6f7ff',
                              boxShadow: 'inset 4px 0 0 #1890ff',
                              outline: '2px solid #91d5ff',
                              outlineOffset: -2,
                            }
                            : undefined;
                  return (
                    <tr key={rowKey} style={pendingRowStyle}>
                      <td style={rowStyle}>{kind === 'product' ? '商品' : (isEndedPass || isPassClosed) ? '日券' : '月曆'}</td>
                      <td style={rowStyle}>
                        <div>{statusLabel}</div>
                        {showRejectMeta && rejectReasonShort ? (
                          <div style={{ fontSize: 11, color: isRejectedPending ? '#cf1322' : '#d48806', marginTop: 2 }} title={rejectReason}>
                            {rejectReasonShort}
                          </div>
                        ) : null}
                      </td>
                      <td style={rowStyle}>{id ?? '—'}</td>
                      <td style={rowStyle}>{r.ACTIVITY_DATE ?? r.activityDate}</td>
                      <td style={rowStyle}>
                        {r.ACTIVITY_TIME ?? r.activityTime}
                        {(r.END_TIME ?? r.endTime) ? ` ~ ${r.END_TIME ?? r.endTime}` : ''}
                      </td>
                      <td style={rowStyle}>
                        {venueMeta?.color ? (
                          <span style={{
                            display: 'inline-block', width: 12, height: 12, borderRadius: 2,
                            background: venueMeta.color, border: '1px solid #ccc', marginRight: 6, verticalAlign: 'middle',
                          }}
                          />
                        ) : null}
                        {formatVenueOptionLabel(venueName, venueMeta?.code)}
                      </td>
                      <td style={rowStyle}>{r.NAME ?? r.name}</td>
                      <td style={rowStyle}>
                        {showRejectMeta
                          ? (organizerDisplay || '—')
                          : (r.ORGANIZER_LOGIN ?? r.organizerLogin ?? '—')}
                      </td>
                      <td style={rowStyle}>
                        {pid ? (
                          <span title={pname || ''}>
                            #{pid}
                            {pslug ? ` (${pslug})` : ''}
                          </span>
                        ) : '—'}
                      </td>
                      <td style={rowStyle}>{submitted || ((isEndedPass || isPassClosed) ? '—' : '')}</td>
                      <td style={rowStyle}>
                        {isEndedPass ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
                            {memberId ? (
                              <span style={{ fontSize: 12, color: '#444' }}>{memberLabel}</span>
                            ) : (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>未綁會員</span>
                            )}
                            {memberId ? (
                              <button type="button" style={btnPrimary} onClick={() => openPassGrantModal(id, 'issue', memberLabel)}>發放日券</button>
                            ) : null}
                            <button type="button" style={btnStyle} onClick={() => handlePassGrantSkip(id)}>這次不發</button>
                          </div>
                        ) : isPassClosed ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: 6, minWidth: 140 }}>
                            <span style={{ fontSize: 12 }}>已發累計 {passQty} 張</span>
                            {memberId ? (
                              <button type="button" style={btnPrimary} onClick={() => openPassGrantModal(id, 'reissue', memberLabel)}>補發日券</button>
                            ) : (
                              <span style={{ fontSize: 12, color: '#9ca3af' }}>未綁會員，無法補發</span>
                            )}
                          </div>
                        ) : (
                          <>
                            <button type="button" style={btnPrimary} onClick={() => openReview(r)}>審核</button>
                            <button type="button" style={btnDanger} onClick={() => openReject(r)}>退回</button>
                          </>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
            </div>
          )}
          {pendingRejectId != null && (
            <div style={{ marginTop: 16, padding: 12, border: '1px solid #dee2e6', borderRadius: 8, background: '#fff' }}>
              <div style={{ marginBottom: 8, fontWeight: 600 }}>退回原因（選填）</div>
              <textarea
                rows={3}
                value={pendingRejectReason}
                onChange={(e) => setPendingRejectReason(e.target.value)}
                style={{ ...textareaStyle, width: '100%', maxWidth: 480 }}
                placeholder="主辦端會看見此說明"
              />
              <div style={{ marginTop: 8 }}>
                <button type="button" style={btnDanger} onClick={rejectPendingCalendar}>確認退回</button>
                <button type="button" style={btnStyle} onClick={() => { setPendingRejectId(null); setPendingRejectKind('calendar'); setPendingRejectReason(''); }}>取消</button>
              </div>
            </div>
          )}
          {passGrantModal && (
            <div style={{
              position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.35)', zIndex: 1200,
              display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16,
            }}
            >
              <div style={{ background: '#fff', borderRadius: 10, padding: 20, width: '100%', maxWidth: 360, boxShadow: '0 8px 24px rgba(0,0,0,0.15)' }}>
                <div style={{ fontWeight: 700, fontSize: 16, marginBottom: 8 }}>
                  {passGrantModal.action === 'reissue' ? '補發日券' : '發放日券'}
                </div>
                {passGrantModal.memberLabel ? (
                  <p style={{ margin: '0 0 12px', fontSize: 13, color: '#555' }}>發至 {passGrantModal.memberLabel}</p>
                ) : null}
                <p style={{ margin: '0 0 14px', fontSize: 14 }}>固定 1 張</p>
                <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end' }}>
                  <button type="button" style={btnStyle} disabled={passGrantSaving} onClick={() => setPassGrantModal(null)}>取消</button>
                  <button type="button" style={btnPrimary} disabled={passGrantSaving} onClick={submitPassGrantModal}>
                    {passGrantSaving ? '送出中…' : '確認送出'}
                  </button>
                </div>
              </div>
            </div>
          )}
        </section>
      )}

      <OrganizerReviewDrawer
        open={reviewCalendarId != null}
        calendarId={reviewCalendarId}
        onClose={() => setReviewCalendarId(null)}
        onDone={() => { refreshPending(); loadList(); }}
        adminAuthHeaders={adminAuthHeaders}
        venueList={venueSelectOptions}
        beverageSuggestions={beverageSuggestions}
        handoverSuggestions={handoverSuggestions}
        internalBriefSuggestions={internalBriefSuggestions}
        equipmentSuggestions={equipmentSuggestions}
      />

      <OrganizerProductReviewDrawer
        open={reviewProductId != null}
        productId={reviewProductId}
        onClose={() => setReviewProductId(null)}
        onDone={() => { refreshPending(); loadList(); }}
        adminAuthHeaders={adminAuthHeaders}
      />

      {adminTab === 'organizer_accounts' && (
        <OrganizerAccountsSection adminAuthHeaders={adminAuthHeaders} formSectionStyle={formSectionStyle} />
      )}

      {adminTab === 'organizer_review_notify' && (
        <OrganizerReviewNotifySection adminAuthHeaders={adminAuthHeaders} formSectionStyle={formSectionStyle} />
      )}

      {adminTab === 'organizer_finance' && (
        <OrganizerFinanceSection adminAuthHeaders={adminAuthHeaders} formSectionStyle={formSectionStyle} />
      )}

      {adminTab === 'activities' && (<>

      {waNarrow && (
        <div style={{ display: 'flex', gap: 8, marginBottom: 12 }}>
          <button type="button" style={btnPrimary} onClick={() => { setForm(emptyForm()); setEditingId(null); setDetachSingle(false); setCopyMode(null); setMobileShowForm(true); scrollToTop(); }}>新增活動</button>
          <a
            href="/weekly-activity"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-block',
              padding: '8px 16px',
              background: '#1677ff',
              color: '#fff',
              borderRadius: 6,
              textDecoration: 'none',
              fontSize: 14,
              fontWeight: 500,
              border: 'none',
              cursor: 'pointer',
            }}
          >
            📅 前往月曆
          </a>
          {mobileShowForm && (
            <button type="button" style={btnStyle} onClick={() => { setMobileShowForm(false); setEditingId(null); setDetachSingle(false); setCopyMode(null); setForm(emptyForm()); }}>關閉表單</button>
          )}
        </div>
      )}

      {/* ===== 產出週活動圖 + 場地顏色管理 並排 ===== */}
      <div className="wa-top-split" style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 16, marginBottom: 16, alignItems: 'start' }}>

      {/* 左：產出週活動圖 */}
      <section style={{ ...formSectionStyle, marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: imgExpanded ? 12 : 0 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0, fontSize: 18 }}>📷 產出週活動圖</h2>
          <button
            type="button"
            onClick={() => setImgExpanded(prev => !prev)}
            style={{ ...btnStyle, fontSize: 13, padding: '4px 12px' }}
          >
            {imgExpanded ? '收合 ▲' : '展開 ▼'}
          </button>
        </div>
        {imgExpanded && (
          <div>
            <p style={{ fontSize: 13, color: '#666', marginBottom: 12 }}>
              選擇任意日期，系統會自動計算該週（週一～週日），將已發布的活動產成單張長圖：畫布高度依內容變長；活動較少時列表區仍維持至少約手機一屏高度，七天列均分垂直空間。底圖會隨畫布高度延伸。
            </p>
            <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 16 }}>
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>選擇日期（自動計算該週）</label>
                <input
                  type="date"
                  value={weekPickerDate}
                  onChange={(e) => { setWeekPickerDate(e.target.value); setPreviewPages([]); }}
                  style={{ ...inputStyle, width: 200 }}
                />
              </div>
              <div>
                <div style={{ fontSize: 13, color: '#666', marginBottom: 6 }}>
                  產出範圍：<strong>{computedWeekRange}</strong>
                </div>
                <button
                  type="button"
                  style={btnPrimary}
                  onClick={generateWeeklyImage}
                  disabled={generating}
                >
                  {generating ? '產生中…' : '🖼️ 產出圖片'}
                </button>
              </div>
              {previewPages.length > 0 && (
                <button type="button" style={btnSuccess} onClick={downloadImage}>
                  ⬇ 下載 PNG
                </button>
              )}
            </div>
            {previewPages.length > 0 && (
              <div style={{ maxWidth: 420, border: '1px solid #ddd', borderRadius: 12, overflow: 'hidden', boxShadow: '0 2px 12px rgba(0,0,0,.08)' }}>
                <img src={previewPages[0]} alt="週活動圖預覽" style={{ width: '100%', display: 'block' }} />
              </div>
            )}
          </div>
        )}
      </section>

      {/* 右：場地顏色管理 */}
      <section style={{ ...formSectionStyle, marginBottom: 0 }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: vcExpanded ? 12 : 0 }}>
          <h2 style={{ marginTop: 0, marginBottom: 0, fontSize: 18 }}>🎨 場地顏色管理</h2>
          <button
            type="button"
            onClick={() => setVcExpanded(prev => !prev)}
            style={{ ...btnStyle, fontSize: 13, padding: '4px 12px' }}
          >
            {vcExpanded ? '收合 ▲' : '展開 ▼'}
          </button>
        </div>
        {vcExpanded && (<>
        {vcVenue ? (
          <div style={{ marginBottom: 10, padding: '8px 12px', background: '#fffbeb', border: '1px solid #fde68a', borderRadius: 8, fontSize: 13, color: '#92400e' }}>
            編輯中：{formatVenueOptionLabel(vcVenue, vcCode) || vcVenue}
          </div>
        ) : null}
        <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap', marginBottom: 12 }}>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>場地</label>
            <select
              value={vcVenue}
              onChange={(e) => {
                const v = e.target.value;
                setVcVenue(v);
                const existing = venueColors.find((vc) => vc.venue === v);
                if (existing) {
                  setVcColor(normalizeHexColor(existing.color));
                  setVcShortName(existing.short_name || '');
                  setVcVenueEn(existing.venue_en || '');
                  setVcShortNameEn(existing.short_name_en || '');
                  setVcCode(existing.code || '');
                  setVcProductId(existing.vbProductId != null ? String(existing.vbProductId) : '');
                }
              }}
              style={{ ...inputStyle, width: '100%' }}
            >
              <option value="">-- 選擇場地 --</option>
              {venueSelectOptions.map((v) => (
                <option key={v.venue} value={v.venue}>{formatVenueOptionLabel(v.venue, v.code)}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>代號</label>
            <input
              type="text"
              value={vcCode}
              onChange={(e) => setVcCode(e.target.value)}
              style={{ ...inputStyle, width: 90 }}
              placeholder="CODE"
              maxLength={50}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>簡稱</label>
            <input
              type="text"
              value={vcShortName}
              onChange={(e) => setVcShortName(e.target.value)}
              style={{ ...inputStyle, width: 80 }}
              placeholder="如：B1"
              maxLength={20}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>場地 (EN)</label>
            <input
              type="text"
              value={vcVenueEn}
              onChange={(e) => setVcVenueEn(e.target.value)}
              style={{ ...inputStyle, width: 140 }}
              placeholder="English"
              maxLength={200}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>簡稱 (EN)</label>
            <input
              type="text"
              value={vcShortNameEn}
              onChange={(e) => setVcShortNameEn(e.target.value)}
              style={{ ...inputStyle, width: 80 }}
              placeholder="EN"
              maxLength={80}
            />
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>綁定包場商品</label>
            <select
              value={vcProductId}
              onChange={(e) => setVcProductId(e.target.value)}
              style={{ ...inputStyle, width: 220 }}
            >
              <option value="">不綁定</option>
              {rentalProducts.map((p) => {
                const pid = p.id ?? p.ID;
                return (
                  <option key={pid} value={pid}>{p.name ?? p.NAME}{p.slug ? ` (${p.slug})` : ''}</option>
                );
              })}
            </select>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>顏色</label>
            <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
              <input
                type="color"
                value={normalizeHexColor(vcColor)}
                onChange={(e) => setVcColor(e.target.value)}
                style={{ width: 50, height: 36, border: '1px solid #ccc', borderRadius: 6, cursor: 'pointer', padding: 2 }}
              />
              <input
                type="text"
                value={vcColor}
                onChange={(e) => setVcColor(e.target.value)}
                onBlur={() => setVcColor(normalizeHexColor(vcColor))}
                style={{ ...inputStyle, width: 96 }}
                placeholder="#4ade80"
              />
            </div>
          </div>
          <button type="button" style={btnPrimary} onClick={handleVcSave} disabled={vcSaving}>
            {vcSaving ? '儲存中…' : '儲存'}
          </button>
        </div>
        {venueColors.length > 0 && (
          <div style={{ ...tableWrap, marginBottom: 0 }}>
          <table style={{ ...tableStyle, width: '100%', minWidth: 520 }}>
            <thead>
              <tr style={{ background: '#f1f3f5' }}>
                <th style={thTdStyle}>場地</th>
                <th style={thTdStyle}>綁定</th>
                <th style={thTdStyle}>操作</th>
              </tr>
            </thead>
            <tbody>
              {venueColors.map((vc) => (
                <tr key={vc.id}>
                  <td style={thTdStyle}>
                    <span style={{ display: 'inline-block', width: 16, height: 16, borderRadius: 3, background: vc.color, border: '1px solid #ccc', verticalAlign: 'middle', marginRight: 8 }} />
                    {formatVenueOptionLabel(vc.venue, vc.code)}
                  </td>
                  <td style={thTdStyle}>
                    {vc.vbProductId != null
                      ? (rentalProductNameById[vc.vbProductId] || `#${vc.vbProductId}`)
                      : '未綁定'}
                  </td>
                  <td style={thTdStyle}>
                    <button
                      type="button"
                      style={{ ...btnStyle, marginRight: 6 }}
                      onClick={() => {
                        setVcVenue(vc.venue || '');
                        setVcColor(normalizeHexColor(vc.color));
                        setVcShortName(vc.short_name || '');
                        setVcVenueEn(vc.venue_en || '');
                        setVcShortNameEn(vc.short_name_en || '');
                        setVcCode(vc.code || '');
                        setVcProductId(vc.vbProductId != null ? String(vc.vbProductId) : '');
                        setVcExpanded(true);
                      }}
                    >
                      編輯
                    </button>
                    <button type="button" style={btnDanger} onClick={() => handleVcDelete(vc.id)}>刪除</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        )}
        </>)}
      </section>

      </div>{/* 並排 grid 結束 */}

      {(!waNarrow || mobileShowForm) && (
      <section
        style={{
          ...formSectionStyle,
          ...(editingId
            ? { background: '#fffbeb', borderColor: '#fde68a' }
            : { background: '#f0fdf4', borderColor: '#bbf7d0' }),
        }}
      >
        <div
          style={{
            margin: '-4px -4px 16px -4px',
            marginTop: -8,
            padding: '10px 14px',
            borderRadius: 8,
            fontSize: 16,
            fontWeight: 700,
            letterSpacing: 0.02,
            ...(editingId
              ? { background: '#fef3c7', color: '#92400e', border: '1px solid #fcd34d' }
              : { background: '#dcfce7', color: '#166534', border: '1px solid #86efac' }),
          }}
        >
          <span>{editingId ? '編輯中' : (copyMode ? '複製為新活動' : '新增活動')}</span>
          <ActivitySourceBadge source={editingId ? form : { organizer_id: null }} />
        </div>
        {detachSingle && editingId && (
          <div style={{ marginBottom: 14, padding: '10px 12px', borderRadius: 8, background: '#fff7ed', border: '1px solid #fdba74', color: '#9a3412', fontSize: 14 }}>
            只編輯這一場，儲存後會脫離同組
          </div>
        )}
        {copyMode && !editingId && (
          <div
            role="status"
            style={{
              marginBottom: 14,
              padding: '10px 12px',
              borderRadius: 8,
              background: '#eff6ff',
              border: '1px solid #93c5fd',
              color: '#1e40af',
              fontSize: 14,
              lineHeight: 1.5,
            }}
          >
            複製自「{copyMode.sourceName}」· 請選擇新日期（時段已預填）· 儲存後為新活動
          </div>
        )}
        <style>{`
          @media (max-width: 900px) {
            .wa-form-split { grid-template-columns: 1fr !important; }
            .wa-time-slot-row {
              flex-direction: column !important;
              align-items: stretch !important;
            }
            .wa-time-slot-row > span:first-child {
              align-self: flex-start !important;
            }
            .wa-time-slot-field {
              width: 100% !important;
            }
            .wa-time-slot-field input,
            .wa-time-slot-field .ant-select {
              width: 100% !important;
              max-width: none !important;
            }
            .wa-venue-time-pair {
              flex-wrap: wrap !important;
              width: 100% !important;
            }
            .wa-venue-time-pair > * {
              flex: 1 1 100% !important;
              min-width: 0 !important;
            }
            .wa-top-split { grid-template-columns: 1fr !important; }
          }
        `}</style>
        <div className="wa-form-split" style={formSplitStyle}>
        <form onSubmit={handleSubmit} style={formGridStyle}>
          {/* ── 場地優先 ── */}
          <div style={{ gridColumn: '1 / -1' }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>場地 *</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              {selectedVenueMeta?.color && (
                <span style={{
                  display: 'inline-block', width: 18, height: 18, borderRadius: 4, flexShrink: 0,
                  background: selectedVenueMeta.color, border: '1px solid #ccc',
                }}
                />
              )}
              <select
                value={form.venue}
                onChange={(e) => {
                  const venue = e.target.value;
                  const meta = venueColors.find((vc) => vc.venue === venue);
                  setForm((f) => ({
                    ...f,
                    venue,
                    venueBookingRequested: f.venue_ordered_separately ? false : !!(meta?.vbProductId),
                    timeSlots: (f.timeSlots || []).map((s) => (
                      (!f.venue_ordered_separately && meta?.vbProductId)
                        ? { ...s, time: '', endTime: '', activityTime: '', activityEndTime: '' }
                        : s
                    )),
                  }));
                  setFocusSlotIdx(0);
                }}
                style={{ ...inputStyle, flex: 1 }}
                required
                disabled={hasVenueOrder}
              >
                <option value="">-- 選擇場地 --</option>
                {form.venue && !venueSelectOptions.some((v) => v.venue === form.venue) && (
                  <option value={form.venue}>{form.venue}</option>
                )}
                {venueSelectOptions.map((v) => (
                  <option key={v.venue} value={v.venue}>{formatVenueOptionLabel(v.venue, v.code)}</option>
                ))}
              </select>
            </div>
            {hasVenueOrder && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#999' }}>
                已綁定場地訂單，如需更換場地請取消活動後重新建立
              </div>
            )}
            {venueNeedsBooking && !hasVenueOrder && (
              <div style={{ marginTop: 6, fontSize: 12, color: '#b45309' }}>
                已綁定場地商品：開始時間須選可預約空檔；結束時間可自由設定（不受開放結束限制）
              </div>
            )}
          </div>

          {venueNeedsBooking && (
            <div style={{
              gridColumn: '1 / -1',
              marginTop: 4, padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13,
            }}
            >
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
                <div style={{ fontWeight: 600 }}>
                  可預約時段
                  {form.timeSlots.length > 1 && (
                    <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>
                      （編輯第 {focusSlotIdx + 1} 個時段）
                    </span>
                  )}
                </div>
                <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                  <button type="button" onClick={() => shiftAvailMonth(-1)} style={{ ...btnStyle, padding: '4px 10px' }}>‹</button>
                  <span style={{ fontWeight: 600, minWidth: 72, textAlign: 'center' }}>{availMonth}</span>
                  <button type="button" onClick={() => shiftAvailMonth(1)} style={{ ...btnStyle, padding: '4px 10px' }}>›</button>
                </div>
              </div>
              {availLoading ? (
                <div style={{ color: '#888' }}>載入中…</div>
              ) : (
                <>
                  {(() => {
                    const [y, m] = (availMonth || '2026-01').split('-').map(Number);
                    const firstDow = new Date(y, m - 1, 1).getDay();
                    const daysInMonth = new Date(y, m, 0).getDate();
                    const cells = [];
                    for (let i = 0; i < firstDow; i++) cells.push(null);
                    for (let d = 1; d <= daysInMonth; d++) cells.push(d);
                    return (
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: 4, marginBottom: 10 }}>
                        {['日', '一', '二', '三', '四', '五', '六'].map((w) => (
                          <div key={w} style={{ textAlign: 'center', fontSize: 11, color: '#999' }}>{w}</div>
                        ))}
                        {cells.map((d, idx) => {
                          if (d == null) return <div key={`e-${idx}`} />;
                          const dateStr = `${availMonth}-${pad2(d)}`;
                          const st = monthDayStatus[dateStr] || 'open';
                          const selected = focusDate === dateStr;
                          const bg = st === 'full' ? '#f5f5f5' : st === 'partial' ? '#fff7e6' : '#f6ffed';
                          const color = st === 'full' ? '#bbb' : '#333';
                          return (
                            <button
                              key={dateStr}
                              type="button"
                              disabled={st === 'full'}
                              onClick={() => patchFocusSlot({ date: dateStr, time: '', endTime: '', activityTime: '', activityEndTime: '' })}
                              style={{
                                border: selected ? '2px solid #7b4f2f' : '1px solid #e8e8e8',
                                borderRadius: 6,
                                padding: '6px 0',
                                background: bg,
                                color,
                                cursor: st === 'full' ? 'not-allowed' : 'pointer',
                                fontWeight: selected ? 700 : 400,
                                fontSize: 13,
                              }}
                            >
                              {d}
                            </button>
                          );
                        })}
                      </div>
                    );
                  })()}
                  <div style={{ fontSize: 11, color: '#999', marginBottom: 8 }}>
                    綠＝有空檔 · 橘＝部分已佔 · 灰＝全日已滿 · 點時段條選場地開始（結束預設 +4 小時，可改更長或跨日）
                  </div>
                  <div style={{ fontWeight: 600, marginBottom: 6, display: 'flex', justifyContent: 'space-between' }}>
                    <span>當日時段{focusDate ? `（${focusDate}）` : ''}</span>
                    {(focusSlot?.time || focusSlot?.endTime) && (
                      <button
                        type="button"
                        onClick={() => patchFocusSlot({ time: '', endTime: '', activityTime: '', activityEndTime: '' })}
                        style={{ border: 'none', background: 'none', color: '#1677ff', cursor: 'pointer', fontSize: 12 }}
                      >
                        重選時間
                      </button>
                    )}
                  </div>
                  {!focusDate && <div style={{ color: '#999' }}>請先選擇日期</div>}
                  {focusDate && daySlotsClickable.length === 0 && (
                    <div style={{ color: '#999' }}>尚無可用性資料</div>
                  )}
                  {focusDate && daySlotsClickable.length > 0 && (
                    <TimeAvailabilityBar
                      slots={daySlotsClickable}
                      selectedStart={focusSlot?.time || undefined}
                      selectedEnd={focusSlot?.endTime || undefined}
                      slotIntervalMinutes={dayAvailBar.interval}
                      endDayOffset={0}
                      onSlotClick={onAvailSlotClick}
                      slotPickAriaPrefix="場地開始"
                    />
                  )}
                </>
              )}
            </div>
          )}

          {/* ── 時段列表 ── */}
          <div style={{ gridColumn: '1 / -1' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
              <label style={{ fontSize: 14, fontWeight: 600 }}>
                {venueNeedsBooking
                  ? '時段（日期／開始／結束／活動起迄）*'
                  : '時段（日期／活動起迄）*'}
                {form.timeSlots.length > 1 && (
                  <span style={{ fontWeight: 400, color: '#666', marginLeft: 8 }}>
                    共 {form.timeSlots.length} 個時段{venueNeedsBooking ? '（點列可切換上方可用性）' : ''}
                  </span>
                )}
              </label>
            </div>
            {form.timeSlots.map((slot, idx) => {
              const activityOpts = venueNeedsBooking
                ? activityStartOptionsInVenueWindow(
                  slot.time,
                  slot.endTime,
                  slot.date,
                  slot.venueEndDate || null,
                )
                : null;
              const activityEndOpts = venueNeedsBooking && slot.endTime
                ? activityEndOptionsAfterStart(
                  slot.activityTime,
                  slot.endTime,
                  slot.time,
                  slot.date,
                  slot.venueEndDate || null,
                  slot.activityEndDate || null,
                )
                : activityEndOptionsAfterStart(slot.activityTime);
              return (
              <div
                key={idx}
                className="wa-time-slot-row"
                onClick={() => setFocusSlotIdx(idx)}
                style={{
                  display: 'flex', gap: 12, alignItems: 'flex-end', marginBottom: 10, flexWrap: 'wrap',
                  padding: focusSlotIdx === idx && venueNeedsBooking ? '8px 10px' : 0,
                  borderRadius: 6,
                  outline: focusSlotIdx === idx && venueNeedsBooking ? '2px solid #7b4f2f' : 'none',
                  background: focusSlotIdx === idx && venueNeedsBooking ? '#faf6f2' : undefined,
                  cursor: venueNeedsBooking ? 'pointer' : undefined,
                }}
              >
                <span style={{ fontSize: 13, color: '#888', minWidth: 22, textAlign: 'right', alignSelf: 'center' }}>{idx + 1}.</span>
                <div className="wa-time-slot-field">
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4, whiteSpace: 'normal' }}>
                    日期{copyMode && !editingId ? ' *（請選新日期）' : ''}
                  </div>
                  <input
                    type="date"
                    value={slot.date}
                    onChange={(e) => setForm((f) => {
                      const slots = [...f.timeSlots];
                      slots[idx] = {
                        ...slots[idx],
                        date: e.target.value,
                        ...(venueNeedsBooking ? { time: '', endTime: '', activityTime: '', activityEndTime: '' } : {}),
                      };
                      return { ...f, timeSlots: slots };
                    })}
                    onFocus={() => setFocusSlotIdx(idx)}
                    style={{
                      ...inputStyle,
                      width: 160,
                      flex: 'none',
                      ...(copyMode && !editingId && !slot.date
                        ? { borderColor: '#ef4444', boxShadow: '0 0 0 1px #fecaca' }
                        : {}),
                    }}
                    required
                  />
                  {copyMode && !editingId && (slot.time || slot.activityTime) && (
                    <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>已帶入原時段，可改</div>
                  )}
                </div>
                {venueNeedsBooking && (
                <div className="wa-time-slot-field">
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4, whiteSpace: 'normal' }}>開始時間／結束時間（卡場）</div>
                  <div className="wa-venue-time-pair" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <QuarterHourTimeInput
                      value={slot.time}
                      onChange={(v) => setForm((f) => {
                        const slots = [...f.timeSlots];
                        const endTime = v ? defaultVenueEndFromStart(v) : '';
                        slots[idx] = {
                          ...slots[idx],
                          time: v,
                          endTime,
                          activityTime: '',
                          activityEndTime: '',
                        };
                        return { ...f, timeSlots: slots };
                      })}
                      allowedValues={idx === focusSlotIdx ? startAllowed : null}
                    />
                    <span style={{ fontSize: 14, color: '#888' }}>~</span>
                    <QuarterHourTimeInput
                      value={slot.endTime || ''}
                      onChange={(v) => setForm((f) => {
                        const slots = [...f.timeSlots];
                        const start = slots[idx].time;
                        const endTime = start && v
                          ? (() => {
                            const vs = parseHmToMinutes(start);
                            const ve = parseHmToMinutes(v);
                            if (vs == null || ve == null) return v;
                            // 有結束日時不強制鐘面晚於開始；無結束日時仍允許隔日鐘面
                            if (!slots[idx].venueEndDate || slots[idx].venueEndDate === slots[idx].date) {
                              const veAbs = normalizeEndMinutesAfterStart(vs, ve);
                              if (veAbs <= vs) return defaultVenueEndFromStart(start);
                              const dur = veAbs - vs;
                              if (dur > ADMIN_VENUE_MAX_MINUTES) {
                                return defaultVenueEndFromStart(start, ORGANIZER_VENUE_MAX_MINUTES);
                              }
                            }
                            return v;
                          })()
                          : v;
                        slots[idx] = {
                          ...slots[idx],
                          endTime,
                          activityTime: '',
                          activityEndTime: '',
                        };
                        return { ...f, timeSlots: slots };
                      })}
                      allowClear={false}
                      allowedValues={idx === focusSlotIdx ? endAllowed : null}
                    />
                    {slot.time && slot.endTime && !slot.venueEndDate && isCrossMidnightHm(slot.time, slot.endTime) ? (
                      <span style={{ fontSize: 12, color: '#888', whiteSpace: 'nowrap' }}>隔日</span>
                    ) : null}
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#666' }}>場地結束日</span>
                    <input
                      type="date"
                      value={slot.venueEndDate || slot.date || ''}
                      min={slot.date || undefined}
                      onChange={(e) => setForm((f) => {
                        const slots = [...f.timeSlots];
                        const v = e.target.value;
                        slots[idx] = {
                          ...slots[idx],
                          venueEndDate: v && v !== slots[idx].date ? v : '',
                          activityTime: '',
                          activityEndTime: '',
                        };
                        return { ...f, timeSlots: slots };
                      })}
                      style={{ ...inputStyle, width: 150, flex: 'none' }}
                    />
                  </div>
                </div>
                )}
                <div className="wa-time-slot-field">
                  <div style={{ fontSize: 12, color: '#666', marginBottom: 4, whiteSpace: 'normal' }}>活動起迄（月曆）</div>
                  <div className="wa-venue-time-pair" style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                    <QuarterHourTimeInput
                      value={slot.activityTime || ''}
                      onChange={(v) => setForm((f) => {
                        const slots = [...f.timeSlots];
                        const clamped = venueNeedsBooking && slot.time && slot.endTime
                          ? clampActivityStartToVenue(
                            v,
                            slot.time,
                            slot.endTime,
                            slot.date,
                            slot.venueEndDate || null,
                          )
                          : v;
                        const prevAct = slots[idx].activityTime;
                        const keepEnd = slots[idx].activityEndTime
                          && slots[idx].activityEndTime !== prevAct
                          && (!prevAct || slots[idx].activityEndTime > clamped);
                        const activityEndTime = keepEnd
                          ? slots[idx].activityEndTime
                          : defaultActivityEnd(slot.endTime, clamped);
                        slots[idx] = {
                          ...slots[idx],
                          activityTime: clamped,
                          activityEndTime,
                        };
                        return { ...f, timeSlots: slots };
                      })}
                      allowedValues={activityOpts && activityOpts.length ? activityOpts : null}
                    />
                    <span style={{ fontSize: 14, color: '#888' }}>~</span>
                    <QuarterHourTimeInput
                      value={slot.activityEndTime || ''}
                      onChange={(v) => setForm((f) => {
                        const slots = [...f.timeSlots];
                        slots[idx] = { ...slots[idx], activityEndTime: v };
                        return { ...f, timeSlots: slots };
                      })}
                      allowedValues={activityEndOpts.length ? activityEndOpts : null}
                    />
                  </div>
                  <div style={{ marginTop: 6, display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <span style={{ fontSize: 12, color: '#666' }}>活動結束日</span>
                    <input
                      type="date"
                      value={slot.activityEndDate || slot.date || ''}
                      min={slot.date || undefined}
                      max={slot.venueEndDate || undefined}
                      onChange={(e) => setForm((f) => {
                        const slots = [...f.timeSlots];
                        const v = e.target.value;
                        slots[idx] = {
                          ...slots[idx],
                          activityEndDate: v && v !== slots[idx].date ? v : '',
                        };
                        return { ...f, timeSlots: slots };
                      })}
                      style={{ ...inputStyle, width: 150, flex: 'none' }}
                    />
                  </div>
                </div>
                {form.timeSlots.length > 1 && (
                  <button
                    type="button"
                    onClick={(e) => {
                      e.stopPropagation();
                      setForm((f) => ({
                        ...f,
                        timeSlots: f.timeSlots.filter((_, i) => i !== idx),
                      }));
                      setFocusSlotIdx((cur) => (cur >= idx ? Math.max(0, cur - 1) : cur));
                    }}
                    style={{
                      background: 'none', border: 'none', color: '#dc3545',
                      cursor: 'pointer', fontSize: 18, lineHeight: 1, padding: '4px 6px',
                      alignSelf: 'center',
                    }}
                    title="移除此時段"
                  >
                    ✕
                  </button>
                )}
              </div>
              );
            })}
            <button
              type="button"
              onClick={() => setForm((f) => {
                const last = f.timeSlots[f.timeSlots.length - 1] || {
                  id: null,
                  date: todayYmd(),
                  time: '19:30',
                  endTime: defaultVenueEndFromStart('19:30'),
                  activityTime: '19:30',
                  activityEndTime: defaultVenueEndFromStart('19:30'),
                };
                const next = {
                  id: null,
                  date: last.date,
                  time: venueNeedsBooking ? '' : '',
                  endTime: venueNeedsBooking ? '' : '',
                  activityTime: venueNeedsBooking ? '' : (last.activityTime || ''),
                  activityEndTime: venueNeedsBooking ? '' : (last.activityEndTime || ''),
                };
                setFocusSlotIdx(f.timeSlots.length);
                return { ...f, timeSlots: [...f.timeSlots, next] };
              })}
              style={{
                background: 'none', border: '1px dashed #999', borderRadius: 6,
                padding: '6px 14px', cursor: 'pointer', fontSize: 13, color: '#555',
                marginTop: 2,
              }}
            >
              + 新增時段
            </button>
          </div>

          {/* ── 右欄：給齊文／給櫃台 ── */}
          <div style={{
            gridColumn: 2,
            gridRow: 'span 3',
            display: 'flex',
            flexDirection: 'column',
            gap: 12,
          }}>
            <div style={{
              border: '2px solid #2563eb',
              borderRadius: 10,
              padding: '12px 14px',
              background: '#eff6ff',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1d4ed8', marginBottom: 4 }}>給齊文的資訊</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>櫃台自填（主辦送審時亦會寫入此區）</div>
              <SuggestMultilineField
                label="簡述活動內容"
                value={form.internal_brief}
                onChange={(v) => setForm((f) => ({ ...f, internal_brief: v }))}
                suggestions={internalBriefSuggestions}
                placeholder="對內說明"
                inputStyle={inputStyle}
              />
              <EquipmentNeedMultiSelect
                label="需求設備"
                value={form.equipment_need}
                onChange={(v) => setForm((f) => ({ ...f, equipment_need: v }))}
                inputStyle={inputStyle}
              />
            </div>
            <div style={{
              border: '2px solid #28a745',
              borderRadius: 10,
              padding: '12px 14px',
              background: '#f0faf3',
            }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#1b7a36', marginBottom: 4 }}>給櫃台（僅後台）</div>
              <div style={{ fontSize: 12, color: '#64748b', marginBottom: 10 }}>後台填寫 · 主辦看不到</div>
              <SuggestMultilineField
                label="需準備飲品"
                value={form.beverage_need}
                onChange={(v) => setForm((f) => ({ ...f, beverage_need: v }))}
                suggestions={beverageSuggestions}
                placeholder="如：紅茶 x20、咖啡 x10"
                inputStyle={inputStyle}
              />
              <SuggestMultilineField
                label="交接事項"
                value={form.handover_notes}
                onChange={(v) => setForm((f) => ({ ...f, handover_notes: v }))}
                suggestions={handoverSuggestions}
                placeholder="如：需提前準備投影設備"
                inputStyle={inputStyle}
              />
              <div>
                <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>指派主辦帳號</label>
                <select
                  value={form.organizer_id || ''}
                  onChange={(e) => {
                    const oid = e.target.value ? Number(e.target.value) : null;
                    const picked = organizerOptions.find((o) => (o.ID ?? o.id) === oid);
                    const dn = picked
                      ? (picked.DISPLAY_NAME || picked.displayName || picked.LOGIN || picked.login || '')
                      : '';
                    setForm((f) => ({
                      ...f,
                      organizer_id: oid,
                      // 有選帳號且顯示名稱空白時，帶入帳號顯示名；解除綁定不清顯示名稱
                      organizer: oid && !String(f.organizer || '').trim() ? dn : f.organizer,
                    }));
                  }}
                  style={inputStyle}
                >
                  <option value="">不綁定（不會出現在主辦後台）</option>
                  {organizerOptions.map((o) => {
                    const oid = o.ID ?? o.id;
                    return (
                      <option key={oid} value={oid}>
                        {o.DISPLAY_NAME || o.displayName || o.name || o.LOGIN || o.login || `主辦 #${oid}`}
                      </option>
                    );
                  })}
                </select>
              </div>
            </div>
          </div>

          {/* ── 左欄 ── */}
          <div>
              <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>主辦（月曆顯示）</label>
              <input
                type="text"
                list="datalist-organizer"
                value={form.organizer}
                onChange={(e) => setForm((f) => ({ ...f, organizer: e.target.value }))}
                style={inputStyle}
                placeholder="與帳號綁定無關，僅顯示用"
              />
            <datalist id="datalist-organizer">
              {[...new Set(list.map((r) => r.organizer).filter(Boolean))].map((o) => (
                <option key={o} value={o} />
              ))}
            </datalist>
          </div>
          {/* 右欄已被綠色框 span 佔用 */}

          {/* ── 左欄 ── */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>活動名稱（可選曾填過的或直接輸入）</label>
            <input
              type="text"
              list="datalist-name"
              value={form.name}
              onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
              style={inputStyle}
              placeholder="輸入或選擇活動名稱"
              required
            />
            <datalist id="datalist-name">
              {[...new Set(list.map((r) => r.name).filter(Boolean))].map((n) => (
                <option key={n} value={n} />
              ))}
            </datalist>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>活動名稱 (EN)</label>
            <input
              type="text"
              value={form.name_en || ''}
              onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
              style={inputStyle}
              placeholder="English name"
            />
          </div>
          {/* 右欄已被綠色框 span 佔用 */}

          {/* ── 左欄 ── */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>消費方式（可選曾填過的或直接輸入）</label>
            <input
              type="text"
              list="datalist-consumption"
              value={form.consumption_type}
              onChange={(e) => setForm((f) => ({ ...f, consumption_type: e.target.value }))}
              style={inputStyle}
              placeholder="輸入或選擇消費方式"
            />
            <datalist id="datalist-consumption">
              {consumptionSuggestions.map((c) => (
                <option key={c} value={c} />
              ))}
            </datalist>
          </div>
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>報名連結</label>
            <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
              <input
                type="text"
                inputMode="url"
                autoComplete="url"
                value={form.registration_link || ''}
                onChange={(e) => setForm((f) => ({ ...f, registration_link: e.target.value }))}
                style={{ ...inputStyle, flex: 1 }}
                placeholder="https://"
              />
              {form.registration_link ? (
                <button
                  type="button"
                  onClick={() => setForm((f) => ({ ...f, registration_link: '' }))}
                  style={{
                    padding: '8px 12px', borderRadius: 6, border: '1px solid #ccc',
                    background: '#fff', cursor: 'pointer', fontSize: 13, whiteSpace: 'nowrap',
                  }}
                >
                  清除
                </button>
              ) : null}
            </div>
          </div>
          {/* ── 右欄 ── */}
          <div>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>公開狀態（未勾選等於草稿）</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 0' }}>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={form.is_public}
                  onChange={(e) => setForm((f) => ({ ...f, is_public: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                顯示在行事曆上
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={form.is_internal}
                  onChange={(e) => setForm((f) => ({ ...f, is_internal: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                顯示在櫃台系統上
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={!!form.occupies_full_venue}
                  onChange={(e) => setForm((f) => ({ ...f, occupies_full_venue: e.target.checked }))}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                占用整個場地（影響樓層開放推算）
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', fontSize: 14 }}>
                <input
                  type="checkbox"
                  checked={!!form.venue_ordered_separately}
                  onChange={(e) => {
                    const on = e.target.checked;
                    setForm((f) => {
                      const meta = venueColors.find((vc) => vc.venue === f.venue);
                      return {
                        ...f,
                        venue_ordered_separately: on,
                        venueBookingRequested: on ? false : !!(meta?.vbProductId),
                        timeSlots: on
                          ? (f.timeSlots || []).map((s) => ({
                            ...s,
                            time: '',
                            endTime: '',
                          }))
                          : f.timeSlots,
                      };
                    });
                  }}
                  style={{ width: 18, height: 18, cursor: 'pointer' }}
                />
                場地已另外下單（不經活動綁定包場／審核）
              </label>
            </div>
          </div>
          <div style={formFullWidth}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>活動介紹</label>
            <textarea
              value={form.detail}
              onChange={(e) => setForm((f) => ({ ...f, detail: e.target.value }))}
              style={{ ...textareaStyle, marginBottom: 0 }}
            />
          </div>
          <div style={formFullWidth}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>活動流程</label>
            <textarea
              value={form.activity_flow || ''}
              onChange={(e) => setForm((f) => ({ ...f, activity_flow: e.target.value }))}
              style={{ ...textareaStyle, marginBottom: 0 }}
            />
          </div>
          <div style={{ ...formFullWidth, marginBottom: 0 }}>
            <label style={{ display: 'block', marginBottom: 4, fontSize: 14 }}>圖片上傳（可多張）</label>
            <input
              type="file"
              accept="image/*"
              onChange={addImageUpload}
              disabled={uploading}
              style={{ marginBottom: 8 }}
            />
            {uploading && <span style={{ marginLeft: 8, color: '#666' }}>上傳中…</span>}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginTop: 8 }}>
              {(form.images || []).map((url, idx) => (
                <div key={idx} style={{ position: 'relative' }}>
                  <img
                    src={url}
                    alt={`預覽 ${idx + 1}`}
                    style={{ width: 100, height: 100, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(idx)}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      width: 24,
                      height: 24,
                      borderRadius: '50%',
                      border: 'none',
                      background: 'rgba(0,0,0,.6)',
                      color: '#fff',
                      cursor: 'pointer',
                      fontSize: 14,
                      lineHeight: 1,
                    }}
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
          <div style={formFullWidth}>
            <button type="submit" disabled={saving} style={btnPrimary}>
              {saving ? '儲存中…' : (editingId
                ? (form.timeSlots.length > 1 ? `儲存 ${form.timeSlots.length} 個時段` : '更新')
                : (copyMode
                  ? '儲存為新活動'
                  : (form.timeSlots.length > 1 ? `新增 ${form.timeSlots.length} 個時段` : '新增')
                )
              )}
            </button>
            {(editingId || copyMode) && (
              <button
                type="button"
                onClick={() => { setForm(emptyForm()); setEditingId(null); setDetachSingle(false); setCopyMode(null); }}
                style={btnStyle}
              >
                {editingId ? '取消編輯' : '取消複製'}
              </button>
            )}
          </div>
        </form>
        <div>
          <h3 style={{ margin: '0 0 10px', fontSize: 16, fontWeight: 700 }}>即時預覽</h3>
          <WeeklyActivityPreviewPanel activity={formPreview} sticky />
        </div>
        </div>
        </section>
      )}

      <section>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12, flexWrap: 'wrap', gap: 8 }}>
          <h2 style={{ margin: 0, fontSize: 18 }}>活動列表</h2>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <button
              type="button"
              onClick={() => changeMonth(-1)}
              style={{ ...btnStyle, padding: '6px 14px', fontSize: 14, fontWeight: 700 }}
            >
              ← 上一月
            </button>
            <span style={{ fontSize: 15, fontWeight: 700, minWidth: 110, textAlign: 'center' }}>{filterRangeLabel}</span>
            <button
              type="button"
              onClick={() => changeMonth(1)}
              style={{ ...btnStyle, padding: '6px 14px', fontSize: 14, fontWeight: 700 }}
            >
              下一月 →
            </button>
          </div>
        </div>

        {waNarrow && (
          <button type="button" style={{ ...btnStyle, marginBottom: 10 }} onClick={() => setFilterExpanded((v) => !v)}>
            {filterRangeLabel} {filterExpanded ? '▴' : '▾'}
          </button>
        )}

        {(!waNarrow || filterExpanded) && (
        <div
          style={{
            marginBottom: 16,
            padding: '14px 16px',
            background: '#f8f9fa',
            border: '1px solid #dee2e6',
            borderRadius: 10,
            display: 'flex',
            flexDirection: 'column',
            gap: 14,
          }}
        >
          <div style={{ fontSize: 13, fontWeight: 700, color: '#495057' }}>篩選明細</div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 24, alignItems: 'flex-start' }}>
            <div style={{ flex: '1 1 280px', maxWidth: 420 }}>
              <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>
                日期區間：在月曆上<strong>按住拖曳</strong>可選連續多日；<strong>單日</strong>則點該日後放開即可（起迄同一天）。
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10, flexWrap: 'wrap' }}>
                <button type="button" onClick={() => shiftFilterCalView(-1)} style={{ ...btnStyle, padding: '4px 12px' }} aria-label="上個月">
                  ◀
                </button>
                <span style={{ fontSize: 15, fontWeight: 700, minWidth: 120, textAlign: 'center' }}>
                  {filterCalView.y} 年 {filterCalView.m0 + 1} 月
                </span>
                <button type="button" onClick={() => shiftFilterCalView(1)} style={{ ...btnStyle, padding: '4px 12px' }} aria-label="下個月">
                  ▶
                </button>
                <button type="button" onClick={applyCurrentMonth} style={{ ...btnStyle, padding: '6px 12px', fontSize: 13 }}>
                  本月
                </button>
              </div>
              <div
                data-filter-range-calendar
                style={{
                  userSelect: 'none',
                  WebkitUserSelect: 'none',
                  touchAction: 'none',
                  border: '1px solid #dee2e6',
                  borderRadius: 10,
                  overflow: 'hidden',
                  background: '#fff',
                }}
              >
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    background: '#e9ecef',
                    fontSize: 12,
                    fontWeight: 600,
                    color: '#495057',
                    textAlign: 'center',
                    padding: '6px 0',
                  }}
                >
                  {['一', '二', '三', '四', '五', '六', '日'].map((w) => (
                    <div key={w}>{w}</div>
                  ))}
                </div>
                <div
                  style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(7, 1fr)',
                    gap: 0,
                  }}
                >
                  {filterCalCells.map(({ ymd, inMonth }) => {
                    const { lo, hi } = displayRangeLoHi;
                    const inRange = lo && hi && ymd >= lo && ymd <= hi;
                    const isEnd = inRange && (ymd === lo || ymd === hi);
                    const isToday = ymd === todayYmd();
                    return (
                      <button
                        key={ymd}
                        type="button"
                        data-range-ymd={ymd}
                        onPointerDown={(e) => beginRangePointerDrag(ymd, e)}
                        style={{
                          minHeight: 40,
                          border: 'none',
                          borderRight: '1px solid #eee',
                          borderBottom: '1px solid #eee',
                          margin: 0,
                          padding: 0,
                          fontSize: 14,
                          cursor: 'pointer',
                          background: inRange ? (isEnd ? '#93c5fd' : '#bfdbfe') : '#fff',
                          color: inMonth ? '#212529' : '#adb5bd',
                          fontWeight: isEnd ? 700 : 500,
                          boxShadow: isToday ? 'inset 0 0 0 2px #0d6efd' : 'none',
                        }}
                      >
                        {parseInt(ymd.slice(8, 10), 10)}
                      </button>
                    );
                  })}
                </div>
              </div>
              <div style={{ fontSize: 12, color: '#868e96', marginTop: 8 }}>
                目前篩選：{filterDateStart || '—'} ~ {filterDateEnd || '—'}
              </div>
            </div>

            <div ref={orgFilterWrapRef} style={{ flex: '1 1 220px', maxWidth: 360, position: 'relative' }}>
              <div style={{ fontSize: 13, color: '#555', marginBottom: 8 }}>主辦人</div>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 13, marginBottom: 8, cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={filterUnboundOrganizer}
                  onChange={(e) => setFilterUnboundOrganizer(e.target.checked)}
                />
                僅未綁定主辦帳號
              </label>
              {uniqueOrganizers.length === 0 ? (
                <span style={{ fontSize: 13, color: '#888' }}>尚無主辦人欄位資料</span>
              ) : (
                <>
                  <button
                    type="button"
                    onClick={() => setOrgFilterOpen((o) => !o)}
                    style={{
                      ...inputStyle,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: 8,
                      cursor: 'pointer',
                      textAlign: 'left',
                      background: '#fff',
                    }}
                  >
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      {filterOrganizerSet.length === 0
                        ? '全部主辦人'
                        : `已選 ${filterOrganizerSet.length} 位：${filterOrganizerSet.join('、')}`}
                    </span>
                    <span style={{ flexShrink: 0, color: '#868e96' }}>{orgFilterOpen ? '▲' : '▼'}</span>
                  </button>
                  {orgFilterOpen && (
                    <div
                      style={{
                        position: 'absolute',
                        zIndex: 50,
                        left: 0,
                        right: 0,
                        marginTop: 4,
                        maxHeight: 240,
                        overflowY: 'auto',
                        background: '#fff',
                        border: '1px solid #ced4da',
                        borderRadius: 8,
                        boxShadow: '0 8px 24px rgba(0,0,0,.12)',
                        padding: '10px 12px',
                      }}
                      onPointerDown={(e) => e.stopPropagation()}
                    >
                      <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 8, gap: 8 }}>
                        {filterOrganizerSet.length > 0 && (
                          <button type="button" onClick={clearOrganizerFilters} style={{ ...btnStyle, padding: '4px 10px', fontSize: 12, marginRight: 0 }}>
                            清除
                          </button>
                        )}
                        <button type="button" onClick={() => setOrgFilterOpen(false)} style={{ ...btnStyle, padding: '4px 10px', fontSize: 12, marginRight: 0 }}>
                          收合
                        </button>
                      </div>
                      {uniqueOrganizers.map((name) => (
                        <label
                          key={name}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 8,
                            cursor: 'pointer',
                            fontSize: 14,
                            padding: '6px 4px',
                            borderRadius: 4,
                          }}
                        >
                          <input
                            type="checkbox"
                            checked={filterOrganizerSet.includes(name)}
                            onChange={() => toggleOrganizerFilter(name)}
                            style={{ width: 16, height: 16, cursor: 'pointer', flexShrink: 0 }}
                          />
                          <span style={{ wordBreak: 'break-all' }}>{name}</span>
                        </label>
                      ))}
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        )}

        {loading ? (
          <p style={{ color: '#666' }}>載入中…</p>
        ) : filteredList.length === 0 ? (
          <p style={{ color: '#666' }}>此篩選條件下尚無活動</p>
        ) : waNarrow ? (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
            {filteredList.map((row) => (
              <div
                key={row.id}
                onClick={() => setDetailItem(row)}
                style={{ background: '#fff', border: '1px solid #e5e7eb', borderRadius: 12, padding: 12 }}
              >
                <div style={{ fontSize: 13, color: '#6b7280' }}>{row.activity_date} · {formatActivityTimeRange(row.activity_time, row.end_time)}</div>
                <div style={{ fontWeight: 800, fontSize: 16, marginTop: 4, wordBreak: 'break-word' }}>{row.name}</div>
                <div style={{ fontSize: 14, color: '#374151', marginTop: 4, wordBreak: 'break-word' }}>{row.venue}</div>
                <div style={{ display: 'flex', gap: 8, marginTop: 10 }} onClick={(e) => e.stopPropagation()}>
                  <button type="button" style={btnStyle} disabled={String(row.status).toLowerCase() === 'cancelled'} onClick={() => handleEdit(row)}>編輯</button>
                  <button type="button" style={btnSuccess} onClick={() => handleCopy(row)}>複製</button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div style={tableWrap}>
            <table style={tableStyle}>
              <thead>
                <tr style={{ background: '#f1f3f5' }}>
                  <th style={thTdStyle}>日期</th>
                  <th style={thTdStyle}>時間</th>
                  <th style={thTdStyle}>名稱</th>
                  <th style={thTdStyle}>場地</th>
                  <th style={thTdStyle}>負責人</th>
                  <th style={thTdStyle}>來源</th>
                  <th style={thTdStyle}>狀態</th>
                  <th style={thTdStyle}>操作</th>
                </tr>
              </thead>
              <tbody>
                {filteredList.map((row) => {
                  const isPub = row.is_public !== undefined ? !!row.is_public : row.status !== 'draft';
                  const isInt = row.is_internal !== undefined ? !!row.is_internal : row.status !== 'draft';
                  const st = String(row.status || '').toLowerCase();
                  const isCancelledRow = st === 'cancelled';
                  const hasSubmitted = !!(row.organizer_submitted_at);
                  const isRejectedRow = st === 'rejected'
                    || (!!row.organizer_reject_reason && !hasSubmitted && !isCancelledRow);
                  const isPendingRow = hasSubmitted && !isCancelledRow;
                  const isDraft = !isPub && !isInt && !isCancelledRow && !isRejectedRow && !isPendingRow;
                  const isPast = row.activity_date < todayYmd();
                  let statusNode = null;
                  if (isCancelledRow) {
                    statusNode = <span style={{ color: '#8c8c8c', fontWeight: 600 }}>已取消</span>;
                  } else if (isPendingRow) {
                    statusNode = <span style={{ color: '#d48806', fontWeight: 600 }}>待審核</span>;
                  } else if (isRejectedRow) {
                    statusNode = <span style={{ color: '#cf1322', fontWeight: 600 }}>退回中</span>;
                  } else if (isDraft) {
                    statusNode = <span style={{ color: '#999' }}>草稿</span>;
                  } else {
                    statusNode = (
                      <div style={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                        {isPub && <span style={{ color: '#28a745' }}>✔ 行事曆</span>}
                        {isInt && <span style={{ color: '#007bff' }}>✔ 櫃台</span>}
                        {row.occupies_full_venue && (
                          <span style={{ color: '#b45309', fontWeight: 700 }}>占用場地</span>
                        )}
                      </div>
                    );
                  }
                  return (
                  <tr
                    key={row.id}
                    style={{
                      ...(isDraft ? { opacity: 0.45, color: '#999' } : {}),
                      ...(isCancelledRow ? {
                        background: '#f5f5f5',
                        boxShadow: 'inset 4px 0 0 #8c8c8c',
                        outline: '2px solid #bfbfbf',
                        outlineOffset: -2,
                      } : {}),
                      ...(isRejectedRow && !isCancelledRow ? {
                        background: '#fff2f0',
                        boxShadow: 'inset 4px 0 0 #ff4d4f',
                        outline: '2px solid #ffa39e',
                        outlineOffset: -2,
                      } : {}),
                      ...(isPast && !isDraft && !isRejectedRow && !isCancelledRow ? { background: '#f0f3f8', color: '#8a9bb5' } : {}),
                      borderBottom: '2px solid #d0d5dd',
                      cursor: 'pointer',
                      transition: 'background .15s',
                    }}
                    onClick={() => setDetailItem(row)}
                    onMouseEnter={(e) => {
                      if (isCancelledRow) e.currentTarget.style.background = '#ebebeb';
                      else if (isRejectedRow) e.currentTarget.style.background = '#ffe7e3';
                      else e.currentTarget.style.background = isPast ? '#e4e9f2' : '#f0f4ff';
                    }}
                    onMouseLeave={(e) => {
                      e.currentTarget.style.background = isCancelledRow
                        ? '#f5f5f5'
                        : isRejectedRow
                          ? '#fff2f0'
                          : (isPast && !isDraft ? '#f0f3f8' : '');
                    }}
                  >
                    <td style={{ ...thTdStyle, whiteSpace: 'nowrap' }}>{row.activity_date}</td>
                    <td style={{ ...thTdStyle, whiteSpace: 'nowrap' }}>
                      {formatActivityTimeRange(row.activity_time, row.end_time)}
                      {(row.venue_product_id || row.VENUE_PRODUCT_ID) && (row.venue_start_time || row.venue_end_time) ? (
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          場地 {row.venue_start_time || '—'}
                          {row.venue_end_time ? ` ~ ${row.venue_end_time}` : ''}
                        </div>
                      ) : null}
                    </td>
                    <td style={{ ...thTdStyle, maxWidth: 220 }}>
                      <div style={{ wordBreak: 'break-word', fontWeight: 600 }}>{row.name}</div>
                    </td>
                    <td style={thTdStyle}>{row.venue}</td>
                    <td style={thTdStyle}>{row.organizer || '—'}</td>
                    <td style={{ ...thTdStyle, whiteSpace: 'nowrap', fontSize: 12 }} onClick={(e) => e.stopPropagation()}>
                      <ActivitySourceBadge source={row} style={{ marginLeft: 0 }} />
                    </td>
                    <td style={{ ...thTdStyle, whiteSpace: 'nowrap', fontSize: 12 }}>
                      {statusNode}
                    </td>
                    <td style={{ ...thTdStyle, whiteSpace: 'nowrap' }} onClick={(e) => e.stopPropagation()}>
                      <button
                        type="button"
                        style={btnStyle}
                        disabled={isCancelledRow}
                        title={isCancelledRow ? '已取消不可編輯，請新建活動' : undefined}
                        onClick={() => handleEdit(row)}
                      >
                        編輯
                      </button>
                      <button type="button" style={btnSuccess} onClick={() => handleCopy(row)}>複製</button>
                      {isPub && (
                        <button
                          type="button"
                          style={{ ...btnStyle, background: '#e6f4ff', color: '#0958d9', borderColor: '#91caff' }}
                          onClick={() => copyActivityLink(row)}
                          title="複製前台活動連結"
                        >
                          🔗 連結
                        </button>
                      )}
                      {row.organizer_id != null ? (
                        <button type="button" style={btnDanger} onClick={() => handleCancelOrganizerActivity(row)} disabled={isCancelledRow}>
                          {isCancelledRow ? '已取消' : '取消'}
                        </button>
                      ) : (
                        <button type="button" style={btnDanger} onClick={() => handleDelete(row.id)}>刪除</button>
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </section>

      {/* ===== 詳情彈窗 ===== */}
      {detailItem && (() => {
        const r = detailItem;
        const isPub = r.is_public !== undefined ? !!r.is_public : r.status !== 'draft';
        const isInt = r.is_internal !== undefined ? !!r.is_internal : r.status !== 'draft';
        const stDetail = String(r.status || '').toLowerCase();
        const isCancelledDetail = stDetail === 'cancelled';
        const hasSubmittedDetail = !!(r.organizer_submitted_at);
        const isRejectedDetail = stDetail === 'rejected'
          || (!!r.organizer_reject_reason && !hasSubmittedDetail && !isCancelledDetail);
        const isPendingDetail = hasSubmittedDetail && !isCancelledDetail;
        const isDraft = !isPub && !isInt && !isCancelledDetail && !isRejectedDetail && !isPendingDetail;
        let detailStatusNode = null;
        if (isCancelledDetail) {
          detailStatusNode = <span style={{ color: '#8c8c8c', fontWeight: 600 }}>已取消</span>;
        } else if (isPendingDetail) {
          detailStatusNode = <span style={{ color: '#d48806', fontWeight: 600 }}>待審核</span>;
        } else if (isRejectedDetail) {
          detailStatusNode = <span style={{ color: '#cf1322', fontWeight: 600 }}>退回中</span>;
        } else if (isDraft) {
          detailStatusNode = <span style={{ color: '#999' }}>草稿</span>;
        } else {
          detailStatusNode = (
            <>
              {isPub && <span style={{ color: '#28a745', marginRight: 10 }}>✔ 行事曆</span>}
              {isInt && <span style={{ color: '#007bff', marginRight: 10 }}>✔ 櫃台</span>}
              {r.occupies_full_venue && (
                <span style={{ color: '#b45309', fontWeight: 700 }}>占用整個場地</span>
              )}
            </>
          );
        }
        return (
          <div
            style={{
              position: 'fixed', inset: 0, zIndex: 9999,
              background: 'rgba(0,0,0,.45)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
            onClick={() => setDetailItem(null)}
          >
            <div
              style={{
                background: '#fff', borderRadius: 14, padding: '28px 32px',
                maxWidth: 560, width: 'min(92%, calc(100vw - 24px))', maxHeight: '85vh',
                overflowY: 'auto', overflowX: 'hidden',
                boxShadow: '0 8px 32px rgba(0,0,0,.18)',
                position: 'relative',
                boxSizing: 'border-box',
                minWidth: 0,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                type="button"
                onClick={() => setDetailItem(null)}
                style={{
                  position: 'absolute', top: 12, right: 14,
                  background: 'none', border: 'none', fontSize: 22, cursor: 'pointer', color: '#888',
                  lineHeight: 1,
                }}
              >✕</button>

              <h3 style={{ margin: '0 0 16px', fontSize: 20, wordBreak: 'break-word' }}>{r.name}</h3>

              <div style={{
                display: 'grid',
                gridTemplateColumns: waNarrow ? 'minmax(0, 1fr)' : 'minmax(72px, 100px) minmax(0, 1fr)',
                gap: '10px 12px',
                fontSize: 14,
                lineHeight: 1.6,
                minWidth: 0,
              }}>
                <span style={{ color: '#888' }}>日期</span>
                <span>{r.activity_date}</span>

                <span style={{ color: '#888' }}>時間</span>
                <span>
                  {formatActivityTimeRange(r.activity_time, r.end_time) || '—'}
                  {(r.venue_product_id || r.VENUE_PRODUCT_ID) && (r.venue_start_time || r.venue_end_time) ? (
                    <span style={{ marginLeft: 8, color: '#64748b', fontSize: 13 }}>
                      （場地 {r.venue_start_time || '—'}
                      {r.venue_end_time ? ` ~ ${r.venue_end_time}` : ''}）
                    </span>
                  ) : null}
                </span>

                <span style={{ color: '#888' }}>場地</span>
                <span>{r.venue}</span>

                <span style={{ color: '#888' }}>主辦（月曆顯示）</span>
                <span>{r.organizer || '—'}</span>

                <span style={{ color: '#888' }}>來源</span>
                <span><ActivitySourceBadge source={r} style={{ marginLeft: 0 }} /></span>

                <span style={{ color: '#888' }}>綁定主辦</span>
                <span>{r.organizer_account_name || '（無）'}</span>

                <span style={{ color: '#888' }}>消費方式</span>
                <span>{r.consumption_type || '—'}</span>

                <span style={{ color: '#888' }}>狀態</span>
                <span>{detailStatusNode}</span>
              </div>

              {/* 給齊文／給櫃台 */}
              {(r.internal_brief || r.equipment_need) && (
                <div style={{
                  marginTop: 16, padding: '10px 14px',
                  border: '2px solid #2563eb', borderRadius: 10, background: '#eff6ff',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1d4ed8', marginBottom: 4 }}>給齊文的資訊</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>主辦／櫃台填寫 · 主辦看得到</div>
                  {r.internal_brief && (
                    <div style={{ fontSize: 14, marginBottom: 4, whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                      <b>簡述活動內容：</b>{r.internal_brief}
                    </div>
                  )}
                  {r.equipment_need && <div style={{ fontSize: 14, marginBottom: 4 }}><b>需求設備：</b>{r.equipment_need}</div>}
                </div>
              )}
              {(r.beverage_need || r.handover_notes) && (
                <div style={{
                  marginTop: 12, padding: '10px 14px',
                  border: '2px solid #28a745', borderRadius: 10, background: '#f0faf3',
                }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: '#1b7a36', marginBottom: 4 }}>給櫃台（僅後台）</div>
                  <div style={{ fontSize: 12, color: '#64748b', marginBottom: 6 }}>後台填寫 · 主辦看不到</div>
                  {r.beverage_need && <div style={{ fontSize: 14, marginBottom: 4 }}><b>需準備飲品：</b>{r.beverage_need}</div>}
                  {r.handover_notes && <div style={{ fontSize: 14 }}><b>交接事項：</b>{r.handover_notes}</div>}
                </div>
              )}

              {(r.detail || r.activity_flow) && (
                <div style={{ marginTop: 14 }}>
                  {r.detail ? (
                    <div style={{ marginBottom: r.activity_flow ? 12 : 0 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 4 }}>活動介紹</div>
                      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f8f9fa', padding: '10px 12px', borderRadius: 8, border: '1px solid #e9ecef' }}>
                        {r.detail}
                      </div>
                    </div>
                  ) : null}
                  {r.activity_flow ? (
                    <div>
                      <div style={{ fontSize: 13, fontWeight: 700, color: '#444', marginBottom: 4 }}>活動流程</div>
                      <div style={{ fontSize: 14, whiteSpace: 'pre-wrap', wordBreak: 'break-word', background: '#f8f9fa', padding: '10px 12px', borderRadius: 8, border: '1px solid #e9ecef' }}>
                        {r.activity_flow}
                      </div>
                    </div>
                  ) : null}
                </div>
              )}

              {/* 圖片 */}
              {r.images && r.images.length > 0 && (
                <div style={{ marginTop: 14 }}>
                  <div style={{ fontSize: 13, color: '#888', marginBottom: 6 }}>圖片</div>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                    {r.images.map((url, i) => (
                      <a key={i} href={url} target="_blank" rel="noopener noreferrer">
                        <img src={url} alt={`圖${i + 1}`} style={{ width: 80, height: 80, objectFit: 'cover', borderRadius: 8, border: '1px solid #ddd' }} />
                      </a>
                    ))}
                  </div>
                </div>
              )}

              {/* 操作按鈕 */}
              <div style={{ marginTop: 20, display: 'flex', gap: 8 }}>
                <button
                  type="button"
                  style={btnPrimary}
                  disabled={isCancelledDetail}
                  title={isCancelledDetail ? '已取消不可編輯，請新建活動' : undefined}
                  onClick={() => { handleEdit(r); setDetailItem(null); }}
                >
                  ✏️ 編輯
                </button>
                <button type="button" style={btnSuccess} onClick={() => { handleCopy(r); setDetailItem(null); }}>📋 複製</button>
                {r.organizer_id != null ? (
                  <button
                    type="button"
                    style={btnDanger}
                    disabled={String(r.status) === 'cancelled'}
                    onClick={() => { handleCancelOrganizerActivity(r); setDetailItem(null); }}
                  >
                    {String(r.status) === 'cancelled' ? '已取消' : '取消活動'}
                  </button>
                ) : (
                  <button type="button" style={btnDanger} onClick={() => { handleDelete(r.id); setDetailItem(null); }}>🗑 刪除</button>
                )}
                <button type="button" style={{ ...btnStyle, marginLeft: 'auto' }} onClick={() => setDetailItem(null)}>關閉</button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* 隱藏 Canvas（用於繪製週活動圖） */}
      <canvas ref={canvasRef} style={{ display: 'none' }} />

      {batchEditChoice && (
        <div
          style={{ position: 'fixed', inset: 0, zIndex: 9000, background: 'rgba(0,0,0,.35)', display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 16 }}
          onClick={() => setBatchEditChoice(null)}
        >
          <div
            onClick={(e) => e.stopPropagation()}
            style={{ background: '#fff', borderRadius: 12, padding: 20, width: 'min(420px, 100%)', boxShadow: '0 12px 40px rgba(0,0,0,.18)' }}
          >
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 14 }}>這個活動有 {batchEditChoice.list.length} 個場次</div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <button
                type="button"
                style={btnPrimary}
                onClick={() => {
                  const { item, list } = batchEditChoice;
                  setBatchEditChoice(null);
                  openActivityForm(list, item.batch_group, { focusId: item.id });
                }}
              >
                編輯全部場次（{batchEditChoice.list.length} 場）
              </button>
              <button
                type="button"
                style={btnStyle}
                onClick={() => {
                  const { item, list } = batchEditChoice;
                  const row = list.find((r) => String(r.id) === String(item.id)) || item;
                  setBatchEditChoice(null);
                  openActivityForm([row], null, { detach: true, focusId: row.id });
                }}
              >
                只編輯這一場
              </button>
              <button type="button" style={btnStyle} onClick={() => setBatchEditChoice(null)}>取消</button>
            </div>
          </div>
        </div>
      )}

      </>)}
    </div>
  );
}
